import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Plus, Package, Search, Filter, FileDown, FileUp, Printer, 
  AlertCircle, Calendar, DollarSign, MapPin, Trash2, Edit2,
  Check, X, ChevronDown, ChevronRight, Archive, CheckSquare, Square, Smile,
  ShoppingCart, Info, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import EmojiPicker from 'emoji-picker-react';

// Smart backend URL detection for different environments
// Backend URL no longer needed - using embedded data
import { loadTemplates } from '../data/loadTemplates';
import inventoryCategories from '../data/inventoryCategories.json';
import inventoryRecommendations from '../data/inventoryRecommendations.json';
import ToolLayout from '../components/ToolLayout';

const InventoryTool = () => {
  const [view, setView] = useState('kits'); // 'kits' or 'master'
  const [kits, setKits] = useState([]);
  const [templates, setTemplates] = useState({});
  const [templatesBySource, setTemplatesBySource] = useState({});
  const [categories, setCategories] = useState([]);
  const [recommendations, setRecommendations] = useState({});
  const [selectedKit, setSelectedKit] = useState(null);
  const [showAddKit, setShowAddKit] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterExpiration, setFilterExpiration] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printType, setPrintType] = useState(''); // 'kit', 'master', 'shopping'
  const printRef = useRef();
  const [householdSize, setHouseholdSize] = useState(() => {
    const saved = localStorage.getItem('inventory_household_size');
    return saved ? parseInt(saved, 10) : 4;
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkLocation, setBulkLocation] = useState('');
  const [taskFilter, setTaskFilter] = useState('all'); // 'all', 'shopping', 'maintenance', 'week', 'month'
  const creatingGeneralStock = useRef(false); // Prevent duplicate creation
  const [expandedKits, setExpandedKits] = useState(new Set()); // Track which parent kits are expanded
  const [editingKit, setEditingKit] = useState(null); // Kit being edited
  const [drillDownView, setDrillDownView] = useState(null); // 'expired', 'expiring', 'lowStock', 'maintenanceOverdue', 'maintenanceDue'
  
  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferItem, setTransferItem] = useState(null);
  const [transferDestinationKit, setTransferDestinationKit] = useState('');
  const [transferQuantity, setTransferQuantity] = useState(1);
  const [transferType, setTransferType] = useState('move'); // 'move' or 'copy'
  
  // Template preview modal
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const [selectedTemplateItems, setSelectedTemplateItems] = useState({});
  const [activeConfigTier, setActiveConfigTier] = useState(1); // Default to Base Kit (index 1)

  // New kit form state
  const [newKit, setNewKit] = useState({
    name: '',
    description: '',
    icon: '📦',
    category: 'Custom',
    location: '',
    parent_kit_id: null,
    items: []
  });

  // Custom units state - load from localStorage
  const [customUnits, setCustomUnits] = useState(() => {
    const saved = localStorage.getItem('inventory_custom_units');
    return saved ? JSON.parse(saved) : [];
  });
  const [showCustomUnitInput, setShowCustomUnitInput] = useState(false);
  const [customUnitInputValue, setCustomUnitInputValue] = useState('');
  const [customUnitContext, setCustomUnitContext] = useState(null); // 'newItem' or 'editItem'

  // Custom Templates state - load from localStorage
  const [customTemplates, setCustomTemplates] = useState(() => {
    const saved = localStorage.getItem('inventory_templates');
    return saved ? JSON.parse(saved) : [];
  });
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showImportTemplate, setShowImportTemplate] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [collapsedOrgs, setCollapsedOrgs] = useState(new Set()); // Track collapsed organizations in template selection
  const [collapsedTemplateOrgs, setCollapsedTemplateOrgs] = useState(new Set()); // Track collapsed orgs in template manager
  const [showEditOrganization, setShowEditOrganization] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);

  // New item form state
  const [newItem, setNewItem] = useState({
    name: '',
    category: 'Food',
    subcategory: '',
    quantity: 1,
    unit: 'items',
    location: '',
    purchase_date: '',
    expiration_date: '',
    cost: 0,
    notes: '',
    low_stock_threshold: 0,
    maintenance_schedules: [], // Array of {id, type, interval_days, notes, last_done, next_due}
    product_url: ''
  });

  // Predefined units
  const predefinedUnits = ['items', 'bottles', 'cans', 'gallons', 'liters', 'pounds', 'ounces', 'boxes', 'rolls', 'packs'];
  
  // Combined units list (predefined + custom)
  const allUnits = [...predefinedUnits, ...customUnits];
  
  // Save custom units to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('inventory_custom_units', JSON.stringify(customUnits));
  }, [customUnits]);
  
  // Handle adding a new custom unit
  const addCustomUnit = (unitName, context) => {
    const trimmedUnit = unitName.trim().toLowerCase();
    
    // Validation
    if (!trimmedUnit) {
      toast.error('Unit name cannot be empty');
      return false;
    }
    
    // Check if already exists in predefined or custom units
    if (predefinedUnits.includes(trimmedUnit) || customUnits.includes(trimmedUnit)) {
      toast.error('This unit already exists');
      return false;
    }
    
    // Add to custom units
    const newCustomUnits = [...customUnits, trimmedUnit];
    setCustomUnits(newCustomUnits);
    
    // Auto-select the new unit for the current item being edited/created
    if (context === 'newItem') {
      setNewItem({ ...newItem, unit: trimmedUnit });
    } else if (context === 'editItem') {
      setEditingItem({ ...editingItem, unit: trimmedUnit });
    }
    
    // Reset input state
    setShowCustomUnitInput(false);
    setCustomUnitInputValue('');
    setCustomUnitContext(null);
    
    toast.success(`Custom unit "${trimmedUnit}" added!`);
    return true;
  };
  
  // Handle unit dropdown change
  const handleUnitChange = (value, context) => {
    if (value === '__add_custom__') {
      setShowCustomUnitInput(true);
      setCustomUnitContext(context);
    } else {
      if (context === 'newItem') {
        setNewItem({ ...newItem, unit: value });
      } else if (context === 'editItem') {
        setEditingItem({ ...editingItem, unit: value });
      }
    }
  };

  // ============= TEMPLATE MANAGEMENT FUNCTIONS =============
  
  // Save custom templates to localStorage
  useEffect(() => {
    localStorage.setItem('inventory_templates', JSON.stringify(customTemplates));
  }, [customTemplates]);

  // Create a new template from selected kits
  const createTemplateFromKits = (templateData) => {
    try {
      const newTemplate = {
        id: `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...templateData,
        is_imported: false,
        source: 'user',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const updatedTemplates = [...customTemplates, newTemplate];
      setCustomTemplates(updatedTemplates);
      toast.success('Template created successfully!');
      return newTemplate;
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
      return null;
    }
  };

  // Update an existing template
  const updateTemplate = (templateId, templateData) => {
    try {
      const updatedTemplates = customTemplates.map(t => 
        t.id === templateId 
          ? { ...t, ...templateData, updated_at: new Date().toISOString() }
          : t
      );
      setCustomTemplates(updatedTemplates);
      toast.success('Template updated successfully!');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  // Delete a template
  const deleteTemplate = (templateId) => {
    try {
      const updatedTemplates = customTemplates.filter(t => t.id !== templateId);
      setCustomTemplates(updatedTemplates);
      toast.success('Template deleted successfully!');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Update organization details for all templates in that organization
  const updateOrganization = (oldOrgId, newOrgData) => {
    try {
      const updatedTemplates = customTemplates.map(template => {
        if (template.org_id === oldOrgId) {
          return {
            ...template,
            org_id: newOrgData.org_id,
            org_name: newOrgData.org_name,
            org_description: newOrgData.org_description || '',
            org_website: newOrgData.org_website || '',
            updated_at: new Date().toISOString()
          };
        }
        return template;
      });
      
      setCustomTemplates(updatedTemplates);
      toast.success('Organization updated successfully!');
      setShowEditOrganization(false);
      setEditingOrganization(null);
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error('Failed to update organization');
    }
  };

  // Sync template with latest kit data
  const syncTemplateWithKits = (templateId) => {
    try {
      const template = customTemplates.find(t => t.id === templateId);
      if (!template || !template.tiers) {
        toast.error('Template not found');
        return;
      }

      // Track which tiers were synced and which kits were not found
      const syncResults = {
        synced: 0,
        notFound: 0,
        syncedTiers: [],
        notFoundTiers: []
      };

      // Update each tier with current kit data
      const updatedTiers = template.tiers.map(tier => {
        let matchingKit = null;
        
        // First, try to find by kitId if it exists (most reliable)
        if (tier.kitId) {
          matchingKit = kits.find(kit => kit.id === tier.kitId);
        }
        
        // If no kitId or kit not found by ID, fall back to name matching
        if (!matchingKit) {
          matchingKit = kits.find(kit => 
            !kit.is_general_stock && (
              tier.name.toLowerCase().includes(kit.name.toLowerCase()) ||
              kit.name.toLowerCase().includes(tier.name.toLowerCase())
            )
          );
        }

        if (matchingKit && matchingKit.items) {
          syncResults.synced++;
          syncResults.syncedTiers.push(`"${tier.name}" → "${matchingKit.name}"`);
          
          // Update tier items with current kit items, and store/update kitId
          return {
            ...tier,
            kitId: matchingKit.id, // Store kit ID for future syncs
            items: matchingKit.items.map(item => ({
              name: item.name,
              category: item.category || 'General',
              quantity: item.quantity || 1,
              unit: item.unit || 'items',
              location: item.location || '',
              notes: item.notes || '',
              cost: item.cost || 0,
              purchase_date: item.purchase_date || '',
              expiration_date: item.expiration_date || '',
              product_url: item.product_url || '',
              maintenance_schedules: item.maintenance_schedules || []
            }))
          };
        } else {
          syncResults.notFound++;
          syncResults.notFoundTiers.push(tier.name);
          return tier;
        }
      });

      // Update the template
      const updatedTemplates = customTemplates.map(t => {
        if (t.id === templateId) {
          return {
            ...t,
            tiers: updatedTiers,
            updated_at: new Date().toISOString(),
            last_synced_at: new Date().toISOString()
          };
        }
        return t;
      });

      setCustomTemplates(updatedTemplates);

      // Show detailed feedback
      if (syncResults.synced > 0 && syncResults.notFound === 0) {
        // All tiers synced successfully
        toast.success(
          `✅ Synced ${syncResults.synced} tier(s)!\n${syncResults.syncedTiers.join('\n')}`,
          { duration: 4000 }
        );
      } else if (syncResults.synced > 0 && syncResults.notFound > 0) {
        // Partial sync
        toast.warning(
          `⚠️ Partially synced:\n✅ ${syncResults.synced} tier(s) synced\n❌ ${syncResults.notFound} tier(s) couldn't find matching kits:\n${syncResults.notFoundTiers.map(t => `  • ${t}`).join('\n')}\n\nTip: Rename tiers to match kit names or manually edit the template.`,
          { duration: 6000 }
        );
      } else {
        // No tiers synced
        toast.error(
          `❌ No matching kits found to sync.\n\nTiers that need matching:\n${syncResults.notFoundTiers.map(t => `  • ${t}`).join('\n')}\n\nTip: Tier names should match your kit names. You can:\n1. Rename tiers to match existing kits\n2. Or manually edit the template with the kit items you want`,
          { duration: 8000 }
        );
      }
    } catch (error) {
      console.error('Error syncing template:', error);
      toast.error('Failed to sync template');
    }
  };

  // Import template from JSON
  const importTemplate = (jsonData) => {
    try {
      const template = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      
      // Validate template structure (standard format: name, tiers required)
      if (!template.name || !template.tiers || !Array.isArray(template.tiers)) {
        toast.error('Invalid template format: Template must have "name" and "tiers" fields');
        return false;
      }
      
      // Convert standard template format to custom template format
      // Generate org_id and template_id from template data
      const orgName = template.source || 'Custom';
      const orgId = orgName.toLowerCase().replace(/\s+/g, '_');
      const templateId = template.name.toLowerCase().replace(/\s+/g, '_');
      
      // Check if template already exists (by org_id and template_id)
      const exists = customTemplates.some(
        t => t.org_id === orgId && t.template_id === templateId
      );
      
      if (exists) {
        toast.error('A template with this organization and ID already exists');
        return false;
      }
      
      // Convert to custom template format
      const newTemplate = {
        org_id: orgId,
        org_name: orgName,
        org_description: '',
        org_website: '',
        template_id: templateId,
        template_name: template.name,
        icon: template.icon || '',
        category: template.category || '',
        description: template.description || '',
        notes: template.notes || '',
        tiers: template.tiers,
        id: `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        is_imported: true,
        source: 'user',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const updatedTemplates = [...customTemplates, newTemplate];
      setCustomTemplates(updatedTemplates);
      toast.success('Template imported successfully!');
      return true;
    } catch (error) {
      console.error('Error importing template:', error);
      toast.error('Failed to import template: Invalid JSON format');
      return false;
    }
  };

  // Export template as JSON (standard format matching folder structure)
  const exportTemplateAsJSON = (template) => {
    try {
      // Export in standard template format (matches folder structure)
      const exportData = {
        name: template.template_name || template.name,
        description: template.description || '',
        icon: template.icon || '',
        category: template.category || '',
        source: template.org_name || template.source || '',
        tiers: template.tiers || []
      };
      
      // Include notes if present
      if (template.notes) {
        exportData.notes = template.notes;
      }
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use template_id or generate from name
      const templateId = template.template_id || (template.template_name || template.name || 'template').toLowerCase().replace(/\s+/g, '_');
      a.download = `${templateId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Template exported as JSON!');
    } catch (error) {
      console.error('Error exporting template:', error);
      toast.error('Failed to export template');
    }
  };

  // Export template as Python code for backend
  const exportTemplateAsPython = (template) => {
    try {
      // Validate template has tiers
      if (!template.tiers || template.tiers.length === 0) {
        toast.error('Template has no tiers to export');
        return;
      }

      let pythonCode = `# Template: ${template.template_name}\n`;
      pythonCode += `# Organization: ${template.org_name}\n`;
      pythonCode += `# To add to backend: Copy this code into the KIT_TEMPLATES_BY_SOURCE dictionary in server.py\n\n`;
      pythonCode += `"${template.template_id}": {\n`;
      pythonCode += `    "name": "${template.template_name}",\n`;
      pythonCode += `    "description": "${(template.description || '').replace(/"/g, '\\"').replace(/\n/g, ' ')}",\n`;
      pythonCode += `    "icon": "${template.icon}",\n`;
      pythonCode += `    "category": "${template.category}",\n`;
      pythonCode += `    "source": "${template.org_name}",\n`;
      pythonCode += `    "notes": "${(template.description || '').replace(/"/g, '\\"').replace(/\n/g, ' ')}",\n`;
      pythonCode += `    "tiers": [\n`;
      
      template.tiers.forEach((tier, tierIndex) => {
        pythonCode += `        {\n`;
        pythonCode += `            "name": "${tier.name}",\n`;
        pythonCode += `            "description": "${(tier.description || '').replace(/"/g, '\\"').replace(/\n/g, ' ')}",\n`;
        pythonCode += `            "items": [\n`;
        
        const items = tier.items || [];
        items.forEach((item, itemIndex) => {
          const escapedNote = (item.note || item.notes || '').replace(/"/g, '\\"').replace(/\n/g, ' ');
          pythonCode += `                {"name": "${item.name}", "category": "${item.category}", "quantity": ${item.quantity}, "unit": "${item.unit}", "cost": ${item.cost || 0}, "low_stock_threshold": ${item.low_stock_threshold || 0}, "note": "${escapedNote}", "product_url": "${item.product_url || ''}"`;
          
          if (item.maintenance_schedules && item.maintenance_schedules.length > 0) {
            pythonCode += `, "maintenance_schedules": ${JSON.stringify(item.maintenance_schedules)}`;
          }
          
          pythonCode += `}${itemIndex < items.length - 1 ? ',' : ''}\n`;
        });
        
        pythonCode += `            ]\n`;
        pythonCode += `        }${tierIndex < template.tiers.length - 1 ? ',' : ''}\n`;
      });
      
      pythonCode += `    ]\n`;
      pythonCode += `}\n\n`;
      pythonCode += `# Instructions:\n`;
      pythonCode += `# 1. Open /app/backend/server.py\n`;
      pythonCode += `# 2. Find the KIT_TEMPLATES_BY_SOURCE dictionary\n`;
      pythonCode += `# 3. Add a new organization entry or add to existing one:\n`;
      pythonCode += `#    "${template.org_id}": {\n`;
      pythonCode += `#        "name": "${template.org_name}",\n`;
      pythonCode += `#        "description": "${(template.org_description || '').replace(/"/g, '\\"')}",\n`;
      pythonCode += `#        "website": "${template.org_website || ''}",\n`;
      pythonCode += `#        "templates": {\n`;
      pythonCode += `#            # Paste the template code here\n`;
      pythonCode += `#        }\n`;
      pythonCode += `#    }\n`;
      
      const blob = new Blob([pythonCode], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.org_id}_${template.template_id}_backend.py`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Template exported as Python code!');
    } catch (error) {
      console.error('Error exporting template as Python:', error);
      toast.error('Failed to export template: ' + error.message);
    }
  };

  // ============= END TEMPLATE MANAGEMENT FUNCTIONS =============

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load kits from localStorage (privacy-first, no server storage)
      const savedKits = localStorage.getItem('emergency_inventory_kits');
      let kitsData = savedKits ? JSON.parse(savedKits) : [];
      
      // Check if General Stock kit exists, if not create it (with duplicate prevention)
      const hasGeneralStock = kitsData.some(kit => kit.is_general_stock);
      if (!hasGeneralStock && !creatingGeneralStock.current) {
        creatingGeneralStock.current = true;
        console.log('Creating General Stock kit...');
        const generalStockKit = {
          id: `kit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: 'General Stock',
          description: 'Items not assigned to a specific kit',
          icon: '📦',
          category: 'Stock',
          location: '',
          is_general_stock: true,
          parent_kit_id: null,
          items: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        kitsData = [generalStockKit, ...kitsData];
        localStorage.setItem('emergency_inventory_kits', JSON.stringify(kitsData));
        console.log('General Stock kit created in localStorage');
        creatingGeneralStock.current = false;
      }
      
      // Sort kits to put General Stock first
      kitsData.sort((a, b) => {
        if (a.is_general_stock) return -1;
        if (b.is_general_stock) return 1;
        return 0;
      });
      
      setKits(kitsData);

      // Load templates from embedded data
      const templatesData = loadTemplates();
      setTemplatesBySource(templatesData);

      // Load categories from embedded data
      setCategories(inventoryCategories);

      // Load recommendations from embedded data
      setRecommendations(inventoryRecommendations);

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load inventory data');
      setLoading(false);
    }
  };

  const createKit = async (kitData) => {
    try {
      // Create kit with local ID (no server storage - privacy first!)
      // ALWAYS generate new UUIDs for items to prevent cross-kit linking
      const itemsWithIds = (kitData.items || []).map(item => ({
        ...item,
        id: crypto.randomUUID(), // Always generate new UUID, even if item has existing id
        is_checked: false
      }));
      
      const newKit = {
        ...kitData,
        id: `kit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        items: itemsWithIds
      };
      
      const updatedKits = [...kits, newKit];
      setKits(updatedKits);
      localStorage.setItem('emergency_inventory_kits', JSON.stringify(updatedKits));
      
      console.log('Kit created in localStorage:', newKit);
      toast.success('Kit created successfully');
      return newKit;
    } catch (error) {
      console.error('Error creating kit:', error);
      toast.error('Failed to create kit: ' + error.message);
    }
  };

  const updateKit = async (kitId, kitData) => {
    try {
      // Update kit in localStorage (no server storage - privacy first!)
      const updatedKit = {
        ...kitData,
        id: kitId,
        updated_at: new Date().toISOString()
      };
      
      const updatedKits = kits.map(k => k.id === kitId ? updatedKit : k);
      setKits(updatedKits);
      localStorage.setItem('emergency_inventory_kits', JSON.stringify(updatedKits));
      
      if (selectedKit && selectedKit.id === kitId) {
        setSelectedKit(updatedKit);
      }
      toast.success('Kit updated successfully');
      return updatedKit;
    } catch (error) {
      console.error('Error updating kit:', error);
      toast.error('Failed to update kit');
    }
  };

  // Check for circular dependency when assigning parent
  const wouldCreateCircularDependency = (kitId, parentKitId) => {
    if (!parentKitId || kitId === parentKitId) {
      return kitId === parentKitId; // Kit cannot be its own parent
    }
    
    // Check if parentKitId is a descendant of kitId
    const visited = new Set();
    let currentId = parentKitId;
    
    while (currentId) {
      if (visited.has(currentId)) {
        return true; // Circular reference detected
      }
      if (currentId === kitId) {
        return true; // Would create a cycle
      }
      
      visited.add(currentId);
      
      // Get parent of current kit
      const currentKit = kits.find(k => k.id === currentId);
      if (!currentKit) {
        break;
      }
      currentId = currentKit.parent_kit_id;
    }
    
    return false;
  };

  const deleteKit = async (kitId) => {
    // Check if this is the General Stock kit
    const kitToDelete = kits.find(k => k.id === kitId);
    if (kitToDelete && kitToDelete.is_general_stock) {
      toast.error('Cannot delete General Stock kit');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this kit?')) {
      return;
    }

    try {
      // Delete from localStorage (no server storage - privacy first!)
      const updatedKits = kits.filter(k => k.id !== kitId);
      setKits(updatedKits);
      localStorage.setItem('emergency_inventory_kits', JSON.stringify(updatedKits));
      
      if (selectedKit && selectedKit.id === kitId) {
        setSelectedKit(null);
      }
      toast.success('Kit deleted successfully');
    } catch (error) {
      console.error('Error deleting kit:', error);
      toast.error('Failed to delete kit');
    }
  };

  const handleCleanupKits = async () => {
    if (!window.confirm('This will fix any broken kit hierarchies (circular dependencies or invalid parent references). Continue?')) {
      return;
    }

    try {
      // Fix broken hierarchies in localStorage (no server storage - privacy first!)
      let fixedCount = 0;
      const validKitIds = new Set(kits.map(k => k.id));
      
      const cleanedKits = kits.map(kit => {
        const parent_id = kit.parent_kit_id;
        
        // Fix invalid parent reference
        if (parent_id && !validKitIds.has(parent_id)) {
          fixedCount++;
          return { ...kit, parent_kit_id: null, updated_at: new Date().toISOString() };
        }
        
        // Fix circular dependency
        if (parent_id && wouldCreateCircularDependency(kit.id, parent_id)) {
          fixedCount++;
          return { ...kit, parent_kit_id: null, updated_at: new Date().toISOString() };
        }
        
        return kit;
      });
      
      if (fixedCount > 0) {
        setKits(cleanedKits);
        localStorage.setItem('emergency_inventory_kits', JSON.stringify(cleanedKits));
        toast.success(`Cleanup complete: ${fixedCount} kit(s) fixed`);
      } else {
        toast.success('No issues found - all kits are healthy!');
      }
    } catch (error) {
      console.error('Error running cleanup:', error);
      toast.error('Failed to run cleanup');
    }
  };

  const handleCreateKitFromTemplate = async (templateId) => {
    // Find template from templatesBySource (format: orgId_templateId)
    const [orgId, ...templateIdParts] = templateId.split('_');
    const actualTemplateId = templateIdParts.join('_');
    const template = templatesBySource[orgId]?.templates?.[actualTemplateId];
    if (!template) {
      console.error(`Template not found: ${templateId}`);
      return;
    }

    // Collect all items from all tiers
    const allItems = [];
    if (template.tiers && Array.isArray(template.tiers)) {
      template.tiers.forEach(tier => {
        if (tier.items && Array.isArray(tier.items)) {
          tier.items.forEach(item => {
            allItems.push(item);
          });
        }
      });
    } else if (template.items && Array.isArray(template.items)) {
      // Fallback for old format
      allItems.push(...template.items);
    }

    const kitData = {
      name: template.name,
      description: template.description,
      icon: template.icon,
      category: template.category,
      location: '',
      items: allItems.map(item => {
        // Calculate next maintenance date if applicable
        let nextMaintenanceDate = null;
        if (item.maintenance_schedules && Array.isArray(item.maintenance_schedules) && item.maintenance_schedules.length > 0) {
          const schedule = item.maintenance_schedules[0];
          if (schedule.interval_days > 0) {
            const today = new Date();
            const nextDate = new Date(today);
            nextDate.setDate(nextDate.getDate() + schedule.interval_days);
            nextMaintenanceDate = nextDate.toISOString().split('T')[0];
          }
        }

        return {
          id: crypto.randomUUID(),
          name: item.name,
          category: item.category,
          subcategory: item.subcategory || '',
          quantity: item.quantity || 0,
          unit: item.unit || 'items',
          location: '',
          kit_id: '',
          purchase_date: null,
          expiration_date: null,
          cost: item.cost || 0,
          notes: item.note || item.notes || '',
          low_stock_threshold: item.low_stock_threshold || 0,
          needs_maintenance: item.maintenance_schedules && item.maintenance_schedules.length > 0,
          maintenance_type: item.maintenance_schedules?.[0]?.type || '',
          maintenance_interval_days: item.maintenance_schedules?.[0]?.interval_days || 0,
          last_maintenance_date: null,
          next_maintenance_date: nextMaintenanceDate,
          maintenance_schedules: item.maintenance_schedules || [],
          is_checked: false
        };
      })
    };

    const created = await createKit(kitData);
    if (created) {
      setShowAddKit(false);
      setShowEmojiPicker(false);
      setSelectedKit(created);
    }
  };

  const handleCreateCustomKit = async () => {
    if (!newKit.name.trim()) {
      toast.error('Please enter a kit name');
      return;
    }

    const created = await createKit(newKit);
    if (created) {
      setShowAddKit(false);
      setShowEmojiPicker(false);
      setSelectedKit(created);
      setNewKit({
        name: '',
        description: '',
        icon: '📦',
        category: 'Custom',
        location: '',
        items: []
      });
    }
  };

  const handleEmojiClick = (emojiData) => {
    setNewKit({ ...newKit, icon: emojiData.emoji });
    setShowEmojiPicker(false);
  };

  const handlePrint = (type) => {
    setPrintType(type);
    setShowPrintPreview(true);
    // Delay print to allow preview to render
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const handleClosePrintPreview = () => {
    setShowPrintPreview(false);
    setPrintType('');
  };

  const handleHouseholdSizeChange = (size) => {
    const newSize = Math.max(1, Math.min(20, size)); // Between 1-20 people
    setHouseholdSize(newSize);
    localStorage.setItem('inventory_household_size', newSize.toString());
  };

  const handleToggleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAllItems = () => {
    if (selectedItems.length === selectedKit.items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(selectedKit.items.map(item => item.id));
    }
  };

  const handleBulkAction = () => {
    if (!selectedKit || selectedItems.length === 0) return;

    if (bulkAction === 'move' || bulkAction === 'copy') {
      const firstItem = selectedKit.items.find(item => selectedItems.includes(item.id));
      if (firstItem) {
        handleOpenTransferModal(firstItem, bulkAction);
        setShowBulkActions(false);
      }
    } else if (bulkAction === 'delete') {
      const proceed = window.confirm(
        `Are you sure you want to delete ${selectedItems.length} item(s)?`
      );
      if (!proceed) return;

      const updatedKit = {
        ...selectedKit,
        items: selectedKit.items.filter(item => !selectedItems.includes(item.id))
      };
      updateKit(selectedKit.id, updatedKit);
      setSelectedItems([]);
      setShowBulkActions(false);
      toast.success(`${selectedItems.length} items deleted`);
    } else if (bulkAction === 'category') {
      if (!bulkCategory) {
        toast.error('Please select a category');
        return;
      }
      const updatedKit = {
        ...selectedKit,
        items: selectedKit.items.map(item => 
          selectedItems.includes(item.id)
            ? { ...item, category: bulkCategory, subcategory: '' }
            : item
        )
      };
      updateKit(selectedKit.id, updatedKit);
      setSelectedItems([]);
      setShowBulkActions(false);
      setBulkCategory('');
      toast.success(`${selectedItems.length} items updated`);
    } else if (bulkAction === 'location') {
      const updatedKit = {
        ...selectedKit,
        items: selectedKit.items.map(item => 
          selectedItems.includes(item.id)
            ? { ...item, location: bulkLocation }
            : item
        )
      };
      updateKit(selectedKit.id, updatedKit);
      setSelectedItems([]);
      setShowBulkActions(false);
      setBulkLocation('');
      toast.success(`${selectedItems.length} items updated`);
    }
  };

  const handleCompleteMaintenance = (itemId) => {
    if (!selectedKit) return;

    const item = selectedKit.items.find(i => i.id === itemId);
    if (!item || !item.needs_maintenance) return;

    const today = new Date();
    const nextDate = new Date(today);
    nextDate.setDate(nextDate.getDate() + item.maintenance_interval_days);

    const updatedKit = {
      ...selectedKit,
      items: selectedKit.items.map(i => 
        i.id === itemId
          ? {
              ...i,
              last_maintenance_date: today.toISOString().split('T')[0],
              next_maintenance_date: nextDate.toISOString().split('T')[0]
            }
          : i
      )
    };

    updateKit(selectedKit.id, updatedKit);
    toast.success(`Maintenance logged for ${item.name}. Next: ${nextDate.toLocaleDateString()}`);
  };

  const handleAddItem = () => {
    if (!selectedKit) return;
    
    if (!newItem.name.trim()) {
      toast.error('Please enter an item name');
      return;
    }

    // Check for duplicates in current kit
    const duplicateInKit = selectedKit.items.find(
      item => item.name.toLowerCase() === newItem.name.trim().toLowerCase()
    );

    // Check for duplicates in other kits
    const duplicatesInOtherKits = kits
      .filter(kit => kit.id !== selectedKit.id)
      .flatMap(kit => 
        kit.items
          .filter(item => item.name.toLowerCase() === newItem.name.trim().toLowerCase())
          .map(item => ({ kitName: kit.name, kitIcon: kit.icon }))
      );

    // Show warning if duplicates found
    if (duplicateInKit) {
      const proceed = window.confirm(
        `"${newItem.name}" already exists in this kit.\n\nDo you want to add it anyway?`
      );
      if (!proceed) return;
    } else if (duplicatesInOtherKits.length > 0) {
      const kitsList = duplicatesInOtherKits
        .map(d => `${d.kitIcon} ${d.kitName}`)
        .join(', ');
      const proceed = window.confirm(
        `"${newItem.name}" already exists in:\n${kitsList}\n\nDo you want to add it to "${selectedKit.name}" anyway?`
      );
      if (!proceed) return;
    }

    // Calculate next maintenance date if applicable
    let nextMaintenanceDate = null;
    if (newItem.needs_maintenance && newItem.maintenance_interval_days > 0) {
      const lastDate = newItem.last_maintenance_date ? new Date(newItem.last_maintenance_date) : new Date();
      nextMaintenanceDate = new Date(lastDate);
      nextMaintenanceDate.setDate(nextMaintenanceDate.getDate() + newItem.maintenance_interval_days);
    }

    const item = {
      id: crypto.randomUUID(),
      ...newItem,
      kit_id: selectedKit.id,
      purchase_date: newItem.purchase_date || null,
      expiration_date: newItem.expiration_date || null,
      last_maintenance_date: newItem.last_maintenance_date || null,
      next_maintenance_date: nextMaintenanceDate ? nextMaintenanceDate.toISOString().split('T')[0] : null,
      is_checked: false
    };

    const updatedKit = {
      ...selectedKit,
      items: [...selectedKit.items, item]
    };

    updateKit(selectedKit.id, updatedKit);
    setShowAddItem(false);
    setNewItem({
      name: '',
      category: 'Food',
      subcategory: '',
      quantity: 1,
      unit: 'items',
      location: '',
      purchase_date: '',
      expiration_date: '',
      cost: 0,
      notes: '',
      low_stock_threshold: 0,
      maintenance_schedules: [],
      product_url: ''
    });
  };

  const handleUpdateItem = () => {
    if (!selectedKit || !editingItem) return;

    // Recalculate next maintenance date if applicable
    let updatedItem = { ...editingItem };
    if (updatedItem.needs_maintenance && updatedItem.maintenance_interval_days > 0) {
      const lastDate = updatedItem.last_maintenance_date ? new Date(updatedItem.last_maintenance_date) : new Date();
      const nextDate = new Date(lastDate);
      nextDate.setDate(nextDate.getDate() + updatedItem.maintenance_interval_days);
      updatedItem.next_maintenance_date = nextDate.toISOString().split('T')[0];
    } else if (!updatedItem.needs_maintenance) {
      // Clear maintenance dates if maintenance is disabled
      updatedItem.next_maintenance_date = null;
    }

    const updatedKit = {
      ...selectedKit,
      items: selectedKit.items.map(item => 
        item.id === editingItem.id ? updatedItem : item
      )
    };

    updateKit(selectedKit.id, updatedKit);
    setEditingItem(null);
  };

  const handleDeleteItem = (itemId) => {
    if (!selectedKit) return;
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    const updatedKit = {
      ...selectedKit,
      items: selectedKit.items.filter(item => item.id !== itemId)
    };

    updateKit(selectedKit.id, updatedKit);
  };

  const handleOpenTransferModal = (item, type = 'move') => {
    setTransferItem(item);
    setTransferType(type);
    setTransferQuantity(type === 'move' ? item.quantity : 1);
    setTransferDestinationKit('');
    setShowTransferModal(true);
  };

  const handleTransferItem = async () => {
    if (!transferItem || !transferDestinationKit || !selectedKit) {
      toast.error('Please select a destination kit');
      return;
    }

    if (transferQuantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    if (transferType === 'move' && transferQuantity > transferItem.quantity) {
      toast.error('Cannot move more than available quantity');
      return;
    }

    if (transferDestinationKit === selectedKit.id) {
      toast.error('Cannot transfer to the same kit');
      return;
    }

    try {
      // LocalStorage-based transfer (privacy-first design)
      const itemsToTransfer = selectedItems.length > 0 
        ? selectedKit.items.filter(item => selectedItems.includes(item.id))
        : [transferItem];
      
      let successCount = 0;
      let failCount = 0;
      
      // Get current kits from localStorage
      let updatedKits = [...kits];
      const sourceKitIndex = updatedKits.findIndex(k => k.id === selectedKit.id);
      const destKitIndex = updatedKits.findIndex(k => k.id === transferDestinationKit);
      
      if (sourceKitIndex === -1 || destKitIndex === -1) {
        toast.error('Source or destination kit not found');
        return;
      }
      
      // Transfer each item
      for (const item of itemsToTransfer) {
        try {
          const quantity = selectedItems.length > 0 ? item.quantity : transferQuantity;
          
          // Create transfer record
          const transferRecord = {
            from_kit_id: selectedKit.id,
            from_kit_name: selectedKit.name,
            to_kit_id: transferDestinationKit,
            to_kit_name: updatedKits[destKitIndex].name,
            quantity: quantity,
            transfer_type: transferType,
            date: new Date().toISOString()
          };
          
          // Create item for destination with NEW UUID (critical fix for item independence)
          const destItem = {
            ...item,
            id: crypto.randomUUID(), // Generate new UUID to prevent cross-kit linking
            quantity: quantity,
            kit_id: transferDestinationKit,
            transfer_history: [...(item.transfer_history || []), transferRecord]
          };
          
          // Add to destination kit
          updatedKits[destKitIndex].items.push(destItem);
          updatedKits[destKitIndex].updated_at = new Date().toISOString();
          
          // Update source kit based on transfer type
          if (transferType === 'move') {
            const sourceItemIndex = updatedKits[sourceKitIndex].items.findIndex(i => i.id === item.id);
            if (sourceItemIndex !== -1) {
              if (quantity >= updatedKits[sourceKitIndex].items[sourceItemIndex].quantity) {
                // Remove item completely
                updatedKits[sourceKitIndex].items.splice(sourceItemIndex, 1);
              } else {
                // Reduce quantity
                updatedKits[sourceKitIndex].items[sourceItemIndex].quantity -= quantity;
                if (!updatedKits[sourceKitIndex].items[sourceItemIndex].transfer_history) {
                  updatedKits[sourceKitIndex].items[sourceItemIndex].transfer_history = [];
                }
                updatedKits[sourceKitIndex].items[sourceItemIndex].transfer_history.push(transferRecord);
              }
            }
          } else if (transferType === 'copy') {
            // Add transfer record to source item
            const sourceItemIndex = updatedKits[sourceKitIndex].items.findIndex(i => i.id === item.id);
            if (sourceItemIndex !== -1) {
              if (!updatedKits[sourceKitIndex].items[sourceItemIndex].transfer_history) {
                updatedKits[sourceKitIndex].items[sourceItemIndex].transfer_history = [];
              }
              updatedKits[sourceKitIndex].items[sourceItemIndex].transfer_history.push(transferRecord);
            }
          }
          
          updatedKits[sourceKitIndex].updated_at = new Date().toISOString();
          successCount++;
        } catch (itemError) {
          console.error(`Error transferring item ${item.name}:`, itemError);
          failCount++;
        }
      }
      
      // Save to localStorage
      setKits(updatedKits);
      localStorage.setItem('emergency_inventory_kits', JSON.stringify(updatedKits));
      
      // Update selected kit
      const updatedSelectedKit = updatedKits.find(k => k.id === selectedKit.id);
      if (updatedSelectedKit) {
        setSelectedKit(updatedSelectedKit);
      }
      
      setShowTransferModal(false);
      setSelectedItems([]);
      
      if (successCount > 0) {
        toast.success(`${successCount} item(s) ${transferType === 'move' ? 'moved' : 'copied'} successfully!`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} item(s) failed to ${transferType}`);
      }
    } catch (error) {
      console.error(`Error ${transferType}ing item:`, error);
      toast.error(`Failed to ${transferType} item`);
    }
  };

  const handleBulkTransfer = async (type = 'move') => {
    if (selectedItems.length === 0) {
      toast.error('No items selected');
      return;
    }

    // Use the first selected item to open modal, but handle all selected items
    const firstItem = selectedKit.items.find(item => selectedItems.includes(item.id));
    if (firstItem) {
      handleOpenTransferModal(firstItem, type);
    }
  };

  const handleToggleItemCheck = (itemId) => {
    if (!selectedKit) return;

    const updatedKit = {
      ...selectedKit,
      items: selectedKit.items.map(item => 
        item.id === itemId ? { ...item, is_checked: !item.is_checked } : item
      )
    };

    updateKit(selectedKit.id, updatedKit);
  };

  const handleCompleteAudit = () => {
    if (!selectedKit) return;

    const updatedKit = {
      ...selectedKit,
      last_audit_date: new Date().toISOString()
    };

    updateKit(selectedKit.id, updatedKit);
    toast.success('Audit completed! All items marked as unchecked for next audit.');
    
    // Reset all checkboxes
    setTimeout(() => {
      const resetKit = {
        ...updatedKit,
        items: updatedKit.items.map(item => ({ ...item, is_checked: false }))
      };
      updateKit(selectedKit.id, resetKit);
    }, 500);
  };

  const addMaintenanceSchedule = (isEditing = false) => {
    const newSchedule = {
      id: `maint_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'replace',
      interval_days: 90,
      notes: '',
      last_done: null,
      next_due: null
    };

    if (isEditing) {
      setEditingItem({
        ...editingItem,
        maintenance_schedules: [...(editingItem.maintenance_schedules || []), newSchedule]
      });
    } else {
      setNewItem({
        ...newItem,
        maintenance_schedules: [...(newItem.maintenance_schedules || []), newSchedule]
      });
    }
  };

  const removeMaintenanceSchedule = (scheduleId, isEditing = false) => {
    if (isEditing) {
      setEditingItem({
        ...editingItem,
        maintenance_schedules: (editingItem.maintenance_schedules || []).filter(s => s.id !== scheduleId)
      });
    } else {
      setNewItem({
        ...newItem,
        maintenance_schedules: (newItem.maintenance_schedules || []).filter(s => s.id !== scheduleId)
      });
    }
  };

  const updateMaintenanceSchedule = (scheduleId, field, value, isEditing = false) => {
    const updateSchedules = (schedules) => 
      schedules.map(s => s.id === scheduleId ? { ...s, [field]: value } : s);

    if (isEditing) {
      setEditingItem({
        ...editingItem,
        maintenance_schedules: updateSchedules(editingItem.maintenance_schedules || [])
      });
    } else {
      setNewItem({
        ...newItem,
        maintenance_schedules: updateSchedules(newItem.maintenance_schedules || [])
      });
    }
  };

  const handleLogMaintenance = (itemId, kitId, scheduleId) => {
    const kit = kits.find(k => k.id === kitId);
    if (!kit) return;

    const item = kit.items.find(i => i.id === itemId);
    if (!item) return;

    const today = new Date();
    
    const updatedKit = {
      ...kit,
      items: kit.items.map(i => 
        i.id === itemId 
          ? {
              ...i,
              maintenance_schedules: (i.maintenance_schedules || []).map(schedule => {
                if (schedule.id === scheduleId) {
                  const nextDue = new Date(today);
                  nextDue.setDate(today.getDate() + (schedule.interval_days || 30));
                  return {
                    ...schedule,
                    last_done: today.toISOString(),
                    next_due: nextDue.toISOString()
                  };
                }
                return schedule;
              })
            }
          : i
      )
    };

    updateKit(kitId, updatedKit);
    toast.success(`Maintenance logged for ${item.name}`);
  };

  // Calculate expiration status
  const getExpirationStatus = (expirationDate) => {
    if (!expirationDate) return 'none';
    
    const date = new Date(expirationDate);
    const now = new Date();
    const daysUntilExpiration = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiration < 0) return 'expired';
    if (daysUntilExpiration <= 30) return 'expiring-soon';
    if (daysUntilExpiration <= 90) return 'warning';
    return 'good';
  };

  // Calculate maintenance status for a schedule
  const getMaintenanceStatus = (schedule) => {
    if (!schedule || !schedule.next_due) return 'none';
    
    const nextDate = new Date(schedule.next_due);
    const now = new Date();
    const daysUntilMaintenance = Math.ceil((nextDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilMaintenance < 0) return 'overdue';
    if (daysUntilMaintenance <= 7) return 'due-soon';
    if (daysUntilMaintenance <= 30) return 'upcoming';
    return 'scheduled';
  };

  // Check if item meets recommended quantities
  const checkRecommendation = (item) => {
    // Try to find recommendation by exact name match first
    let recommendation = recommendations[item.name];
    
    // If not found, try category-based recommendations
    if (!recommendation) {
      const categoryRecommendations = {
        'Water': recommendations['Water'],
        'Food': recommendations['Food'],
        'Medical': recommendations['First Aid Kit']
      };
      recommendation = categoryRecommendations[item.category];
    }

    if (!recommendation) return null;

    // Calculate recommended quantity
    let recommendedQty = recommendation.recommended || recommendation.minimum || 0;
    let minQty = recommendation.minimum || 0;

    // For per-person items (water, food), use actual household size
    if (recommendation.per_person_per_day) {
      recommendedQty = recommendation.per_person_per_day * recommendation.recommended_days * householdSize;
      minQty = recommendation.per_person_per_day * recommendation.minimum_days * householdSize;
    }

    const status = item.quantity >= recommendedQty ? 'meets' : 
                   item.quantity >= minQty ? 'minimum' : 'below';

    return {
      status,
      minimum: minQty,
      recommended: recommendedQty,
      source: recommendation.source,
      note: recommendation.note,
      unit: recommendation.unit,
      isPerPerson: !!recommendation.per_person_per_day
    };
  };

  // Get all items across all kits
  const allItems = useMemo(() => {
    return kits.flatMap(kit => 
      kit.items.map(item => ({
        ...item,
        kitId: kit.id,
        kitName: kit.name,
        kitIcon: kit.icon
      }))
    );
  }, [kits]);

  // Flatten all maintenance schedules from all items
  const allMaintenanceSchedules = useMemo(() => {
    return allItems.flatMap(item => 
      (item.maintenance_schedules || []).map(schedule => ({
        ...schedule,
        itemId: item.id,
        itemName: item.name,
        kitId: item.kitId,
        kitName: item.kitName,
        kitIcon: item.kitIcon,
        itemCost: item.cost || 0,
        itemProductUrl: item.product_url
      }))
    );
  }, [allItems]);

  // Get all unique categories (predefined + custom from items)
  const allCategories = useMemo(() => {
    const customCategories = new Set(allItems.map(item => item.category).filter(cat => cat && cat.trim() !== ''));
    const predefinedCategories = categories.map(c => c.name);
    
    // Merge and deduplicate
    const allCats = [...new Set([...predefinedCategories, ...customCategories])];
    return allCats.sort();
  }, [allItems, categories]);

  // Build hierarchical kit structure
  const kitTree = useMemo(() => {
    // Separate top-level kits and subkits
    const topLevel = kits.filter(k => !k.parent_kit_id);
    const subkits = kits.filter(k => k.parent_kit_id);
    
    // Build tree with children
    const buildTree = (parentId) => {
      return subkits.filter(k => k.parent_kit_id === parentId);
    };
    
    return topLevel.map(kit => ({
      ...kit,
      children: buildTree(kit.id).map(child => ({
        ...child,
        children: buildTree(child.id).map(grandchild => ({
          ...grandchild,
          children: buildTree(grandchild.id) // Recursively handle unlimited depth
        }))
      }))
    }));
  }, [kits]);

  // Calculate total item count including subkits recursively
  const getTotalItemCount = (kit) => {
    let count = kit.items?.length || 0;
    if (kit.children) {
      kit.children.forEach(child => {
        count += getTotalItemCount(child);
      });
    }
    return count;
  };

  // Get breadcrumb path for a kit
  const getKitBreadcrumb = (kitId) => {
    const breadcrumb = [];
    let currentKit = kits.find(k => k.id === kitId);
    
    while (currentKit) {
      breadcrumb.unshift(currentKit);
      currentKit = currentKit.parent_kit_id ? kits.find(k => k.id === currentKit.parent_kit_id) : null;
    }
    
    return breadcrumb;
  };

  // Generate shopping list from low stock items
  const shoppingList = useMemo(() => {
    return allItems.filter(item => 
      item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold
    ).sort((a, b) => a.category.localeCompare(b.category));
  }, [allItems]);

  // Generate maintenance tasks lists (from all schedules)
  const maintenanceTasks = useMemo(() => {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    // Filter schedules that need attention
    const activeSchedules = allMaintenanceSchedules.filter(schedule => {
      const status = getMaintenanceStatus(schedule);
      return status === 'overdue' || status === 'due-soon' || status === 'upcoming';
    });
    
    return {
      needsPurchase: activeSchedules.filter(schedule => 
        schedule.type === 'replace' || schedule.type === 'rotate'
      ).sort((a, b) => {
        const aDate = new Date(a.next_due || 0);
        const bDate = new Date(b.next_due || 0);
        return aDate - bDate;
      }),
      
      noPurchase: activeSchedules.filter(schedule => 
        schedule.type === 'test' || schedule.type === 'inspect'
      ).sort((a, b) => {
        const aDate = new Date(a.next_due || 0);
        const bDate = new Date(b.next_due || 0);
        return aDate - bDate;
      }),
      
      thisWeek: activeSchedules.filter(schedule => {
        if (!schedule.next_due) return false;
        const dueDate = new Date(schedule.next_due);
        return dueDate <= sevenDaysFromNow;
      }).sort((a, b) => {
        const aDate = new Date(a.next_due || 0);
        const bDate = new Date(b.next_due || 0);
        return aDate - bDate;
      }),
      
      thisMonth: activeSchedules.filter(schedule => {
        if (!schedule.next_due) return false;
        const dueDate = new Date(schedule.next_due);
        return dueDate <= thirtyDaysFromNow;
      }).sort((a, b) => {
        const aDate = new Date(a.next_due || 0);
        const bDate = new Date(b.next_due || 0);
        return aDate - bDate;
      })
    };
  }, [allMaintenanceSchedules]);

  // Calculate task costs
  const taskCosts = useMemo(() => {
    const restockCost = shoppingList.reduce((sum, item) => {
      // Use minimum of 1 to match the table display logic
      const toBuy = Math.max(item.low_stock_threshold - item.quantity, 1);
      const itemCost = (item.cost || 0) * toBuy;
      console.log(`Restock: ${item.name} - toBuy: ${toBuy}, cost: ${item.cost}, total: ${itemCost}`);
      return sum + itemCost;
    }, 0);
    
    const maintenanceCost = maintenanceTasks.needsPurchase.reduce((sum, item) => {
      const itemCost = item.cost || 0;
      console.log(`Maintenance: ${item.name} - cost: ${itemCost}`);
      return sum + itemCost;
    }, 0);
    
    console.log(`Total costs - Restock: $${restockCost}, Maintenance: $${maintenanceCost}`);
    return { restockCost, maintenanceCost, total: restockCost + maintenanceCost };
  }, [shoppingList, maintenanceTasks]);

  // Filter items for master view
  const filteredItems = useMemo(() => {
    return allItems.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.category.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
      const expirationStatus = getExpirationStatus(item.expiration_date);
      const matchesExpiration = filterExpiration === 'all' || 
                               (filterExpiration === 'expired' && expirationStatus === 'expired') ||
                               (filterExpiration === 'expiring' && (expirationStatus === 'expiring-soon' || expirationStatus === 'warning'));
      
      return matchesSearch && matchesCategory && matchesExpiration;
    });
  }, [allItems, searchTerm, filterCategory, filterExpiration]);

  // Dashboard statistics
  const stats = useMemo(() => {
    const totalItems = allItems.length;
    const totalKits = kits.length;
    const expiredItems = allItems.filter(item => getExpirationStatus(item.expiration_date) === 'expired').length;
    const expiringSoon = allItems.filter(item => {
      const status = getExpirationStatus(item.expiration_date);
      return status === 'expiring-soon' || status === 'warning';
    }).length;
    const totalValue = allItems.reduce((sum, item) => sum + (item.cost || 0), 0);
    const lowStock = allItems.filter(item => item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold).length;
    
    // Maintenance stats (count schedules, not items)
    const maintenanceOverdue = allMaintenanceSchedules.filter(schedule => getMaintenanceStatus(schedule) === 'overdue').length;
    const maintenanceDueSoon = allMaintenanceSchedules.filter(schedule => getMaintenanceStatus(schedule) === 'due-soon').length;

    return { totalItems, totalKits, expiredItems, expiringSoon, totalValue, lowStock, maintenanceOverdue, maintenanceDueSoon };
  }, [allItems, kits, allMaintenanceSchedules]);

  // Category distribution for pie chart
  const categoryData = useMemo(() => {
    const distribution = {};
    allItems.forEach(item => {
      distribution[item.category] = (distribution[item.category] || 0) + 1;
    });
    return Object.entries(distribution).map(([name, value]) => ({ name, value }));
  }, [allItems]);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  const exportData = () => {
    try {
      // Create comprehensive export data with all user state
      const exportPackage = {
        version: "1.0",
        exported_at: new Date().toISOString(),
        data: {
          kits: kits,
          custom_templates: customTemplates
        }
      };
      
      const dataStr = JSON.stringify(exportPackage, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory-complete-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${kits.length} kits and ${customTemplates.length} templates!`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export inventory data');
    }
  };

  const importData = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importedData = JSON.parse(text);
      
      // Check if it's the new format (with version) or legacy format (just kits array)
      let kitsToImport = [];
      let templatesToImport = [];
      
      if (Array.isArray(importedData)) {
        // Legacy format: just an array of kits
        kitsToImport = importedData;
        console.log('Importing legacy format (kits only)');
      } else if (importedData.version && importedData.data) {
        // New format: comprehensive export
        kitsToImport = importedData.data.kits || [];
        templatesToImport = importedData.data.custom_templates || [];
        console.log('Importing new format (kits + templates)');
      } else {
        toast.error('Invalid import file format');
        return;
      }
      
      // Import kits via API
      let kitsImported = 0;
      for (const kit of kitsToImport) {
        try {
          await createKit(kit);
          kitsImported++;
        } catch (error) {
          console.error('Error importing kit:', kit.name, error);
        }
      }
      
      // Import custom templates to localStorage
      if (templatesToImport.length > 0) {
        // Merge with existing templates, avoiding duplicates
        const existingIds = new Set(customTemplates.map(t => t.id));
        const newTemplates = templatesToImport.filter(t => !existingIds.has(t.id));
        
        const updatedTemplates = [...customTemplates, ...newTemplates];
        setCustomTemplates(updatedTemplates);
        localStorage.setItem('inventory_templates', JSON.stringify(updatedTemplates));
      }
      
      // Reload kit data from backend
      await loadData();
      
      // Show success message
      const successMsg = [];
      if (kitsImported > 0) successMsg.push(`${kitsImported} kits`);
      if (templatesToImport.length > 0) successMsg.push(`${templatesToImport.length} templates`);
      
      toast.success(`Imported ${successMsg.join(' and ')}!`);
    } catch (error) {
      console.error('Error importing data:', error);
      toast.error('Failed to import inventory data');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Package className="w-16 h-16 text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-muted">Loading inventory...</p>
        </div>
      </div>
    );
  }

  // Recursive function to render kit tree with expand/collapse
  const renderKitTree = (kit, depth = 0) => {
    const hasChildren = kit.children && kit.children.length > 0;
    const isExpanded = expandedKits.has(kit.id);
    const totalItems = getTotalItemCount(kit);
    
    const expiredCount = kit.items.filter(item => getExpirationStatus(item.expiration_date) === 'expired').length;
    const expiringCount = kit.items.filter(item => {
      const status = getExpirationStatus(item.expiration_date);
      return status === 'expiring-soon' || status === 'warning';
    }).length;

    const toggleExpand = (e) => {
      e.stopPropagation();
      const newExpanded = new Set(expandedKits);
      if (isExpanded) {
        newExpanded.delete(kit.id);
      } else {
        newExpanded.add(kit.id);
      }
      setExpandedKits(newExpanded);
    };

    return (
      <div key={kit.id} style={{ marginLeft: `${depth * 24}px` }}>
        <div
          className={`p-4 rounded-lg transition-shadow cursor-pointer border border-border hover:bg-surface ${
            kit.is_general_stock ? 'bg-primary/10 border-primary' : 'bg-card'
          }`}
          onClick={() => setSelectedKit(kit)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              {/* Expand/Collapse button */}
              {hasChildren && (
                <button
                  onClick={toggleExpand}
                  className="text-text-muted hover:text-text-primary p-1"
                >
                  {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
              )}
              {!hasChildren && <div className="w-7" />}
              
              <div className="text-3xl">{kit.icon}</div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-text-primary flex items-center gap-2">
                  {kit.name}
                  {kit.is_general_stock && (
                    <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">Stock</span>
                  )}
                </h3>
                <p className="text-sm text-foreground/90">{kit.category}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-foreground/90">
                  <span className="font-semibold text-text-primary">{totalItems}</span> items
                  {hasChildren && <span className="text-xs ml-1">(w/ subkits)</span>}
                </div>
                {expiredCount > 0 && <div className="text-xs text-destructive">{expiredCount} expired</div>}
                {expiringCount > 0 && <div className="text-xs text-orange-500">{expiringCount} expiring</div>}
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingKit(kit);
                  }}
                  className="text-text-muted hover:text-primary"
                  title="Edit kit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {!kit.is_general_stock && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteKit(kit.id);
                    }}
                    className="text-text-muted hover:text-destructive"
                    title="Delete kit"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Render children if expanded */}
        {hasChildren && isExpanded && (
          <div className="mt-2 space-y-2">
            {kit.children.map(child => renderKitTree(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Edit Organization Modal Component
  const EditOrganizationModal = ({ organization, onClose, onUpdate }) => {
    const [formData, setFormData] = useState({
      org_id: organization?.org_id || '',
      org_name: organization?.org_name || '',
      org_description: organization?.org_description || '',
      org_website: organization?.org_website || ''
    });

    // Function to generate org_id from org_name
    const generateOrgId = (orgName) => {
      if (!orgName) return '';
      return orgName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
        .trim()
        .replace(/\s+/g, '_'); // Replace spaces with underscores
    };

    // Handle organization name change and auto-generate ID
    const handleOrgNameChange = (newName) => {
      setFormData(prev => ({
        ...prev,
        org_name: newName,
        // Only auto-generate if org_id is empty or was previously auto-generated
        org_id: prev.org_id === '' || prev.org_id === generateOrgId(prev.org_name)
          ? generateOrgId(newName)
          : prev.org_id
      }));
    };

    const handleSubmit = () => {
      // Validation
      if (!formData.org_id || !formData.org_name) {
        toast.error('Organization ID and Name are required');
        return;
      }

      onUpdate(organization.org_id, formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-card rounded-lg max-w-2xl w-full">
          <div className="bg-primary text-primary-foreground p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Edit Organization</h2>
                <p className="text-purple-100 mt-1">Update organization details for all templates</p>
              </div>
              <button
                onClick={onClose}
                className="text-primary-foreground hover:bg-white/20 rounded-full p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-primary/10 border border-border rounded-lg p-4 mb-4">
              <p className="text-sm text-text-primary">
                <strong>Note:</strong> Changing these details will update all templates in this organization.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Organization Name *
                </label>
                <input
                  type="text"
                  value={formData.org_name}
                  onChange={(e) => handleOrgNameChange(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="My Organization"
                />
                <p className="text-xs text-text-muted mt-1">Enter a descriptive name for your organization</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Organization ID * <span className="text-text-muted font-normal">(Auto-generated, editable)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.org_id}
                    onChange={(e) => setFormData({ ...formData, org_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                    className="flex-1 px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="my_organization"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, org_id: generateOrgId(formData.org_name) })}
                    className="px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors text-sm font-medium"
                    title="Regenerate ID from organization name"
                  >
                    🔄
                  </button>
                </div>
                <p className="text-xs text-text-muted mt-1">Lowercase, underscore separated (auto-filled)</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Organization Description
              </label>
              <textarea
                value={formData.org_description}
                onChange={(e) => setFormData({ ...formData, org_description: e.target.value })}
                rows="2"
                className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Describe your organization..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Organization Website
              </label>
              <input
                type="url"
                value={formData.org_website}
                onChange={(e) => setFormData({ ...formData, org_website: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="bg-surface-muted px-6 py-4 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-text-primary hover:bg-surface-muted rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
            >
              Update Organization
            </button>
          </div>
        </div>
      </div>
    );
  };

  // CreateTemplateModal Component
  const CreateTemplateModal = ({ kits, editingTemplate, onClose, onCreate, onUpdate }) => {
    const [formData, setFormData] = useState(editingTemplate || {
      org_id: '',
      org_name: '',
      org_description: '',
      org_website: '',
      template_id: '',
      template_name: '',
      icon: '📦',
      category: 'Custom',
      description: '',
      selectedKits: []
    });
    
    // Get unique organizations from existing custom templates AND backend templates
    const existingOrgs = React.useMemo(() => {
      const orgsMap = {};
      
      // Add custom template organizations
      customTemplates.forEach(t => {
        if (t.org_id && !orgsMap[t.org_id]) {
          orgsMap[t.org_id] = {
            id: t.org_id,
            name: t.org_name,
            description: t.org_description || '',
            website: t.org_website || ''
          };
        }
      });
      
      // Add backend template organizations
      Object.entries(templatesBySource).forEach(([sourceId, sourceData]) => {
        if (!orgsMap[sourceId]) {
          orgsMap[sourceId] = {
            id: sourceId,
            name: sourceData.name,
            description: sourceData.description || '',
            website: sourceData.website || ''
          };
        }
      });
      
      return Object.values(orgsMap);
    }, [customTemplates, templatesBySource]);
    
    // Default to showing existing orgs if they exist and we're not editing
    const [showNewOrg, setShowNewOrg] = useState(!editingTemplate && existingOrgs.length === 0);
    const [showEmojiPickerTemplate, setShowEmojiPickerTemplate] = useState(false);
    const [showCustomCategory, setShowCustomCategory] = useState(false);
    const [customCategoryInput, setCustomCategoryInput] = useState('');
    const [tierConfigs, setTierConfigs] = useState(
      editingTemplate?.tiers?.map((tier, idx) => ({
        kitId: tier.kitId || null, // Preserve kit ID from template
        tierName: tier.name,
        tierDescription: tier.description,
        tierOrder: tier.order || idx + 1,
        items: tier.items
      })) || []
    );

    // Function to generate template_id from template_name
    const generateTemplateId = (templateName) => {
      if (!templateName) return '';
      return templateName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
        .trim()
        .replace(/\s+/g, '_'); // Replace spaces with underscores
    };

    // Function to generate org_id from org_name
    const generateOrgId = (orgName) => {
      if (!orgName) return '';
      return orgName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
        .trim()
        .replace(/\s+/g, '_'); // Replace spaces with underscores
    };

    // Handle template name change and auto-generate ID
    const handleTemplateNameChange = (newName) => {
      setFormData(prev => ({
        ...prev,
        template_name: newName,
        // Only auto-generate if template_id is empty or was previously auto-generated
        template_id: prev.template_id === '' || prev.template_id === generateTemplateId(prev.template_name)
          ? generateTemplateId(newName)
          : prev.template_id
      }));
    };

    // Handle organization name change and auto-generate ID
    const handleOrgNameChange = (newName) => {
      setFormData(prev => ({
        ...prev,
        org_name: newName,
        // Only auto-generate if org_id is empty or was previously auto-generated
        org_id: prev.org_id === '' || prev.org_id === generateOrgId(prev.org_name)
          ? generateOrgId(newName)
          : prev.org_id
      }));
    };

    const handleKitSelection = (kitId) => {
      const kit = kits.find(k => k.id === kitId);
      if (!kit) return;

      const newTierConfig = {
        kitId: kit.id,
        tierName: kit.name,
        tierDescription: kit.description || '',
        tierOrder: tierConfigs.length + 1,
        items: kit.items.map(item => ({
          name: item.name,
          category: item.category,
          quantity: item.quantity,
          unit: item.unit,
          cost: item.cost || 0,
          low_stock_threshold: item.low_stock_threshold || 0,
          note: item.notes || '',
          product_url: item.product_url || '',
          maintenance_schedules: item.maintenance_schedules || []
        }))
      };

      setTierConfigs([...tierConfigs, newTierConfig]);
    };

    const handleRemoveTier = (index) => {
      setTierConfigs(tierConfigs.filter((_, idx) => idx !== index));
    };

    const handleTierChange = (index, field, value) => {
      const updated = [...tierConfigs];
      updated[index][field] = value;
      setTierConfigs(updated);
    };

    const handleSubmit = () => {
      // Validation
      if (!formData.org_id || !formData.org_name) {
        toast.error('Organization ID and Name are required');
        return;
      }
      if (!formData.template_id || !formData.template_name) {
        toast.error('Template ID and Name are required');
        return;
      }
      if (tierConfigs.length === 0) {
        toast.error('Please add at least one tier');
        return;
      }

      const templateData = {
        org_id: formData.org_id,
        org_name: formData.org_name,
        org_description: formData.org_description,
        org_website: formData.org_website,
        template_id: formData.template_id,
        template_name: formData.template_name,
        icon: formData.icon,
        category: formData.category,
        description: formData.description,
        tiers: tierConfigs.map(tier => ({
          name: tier.tierName,
          description: tier.tierDescription,
          order: tier.tierOrder,
          kitId: tier.kitId || null, // Preserve kit ID for reliable sync
          items: tier.items
        }))
      };

      if (editingTemplate) {
        onUpdate(editingTemplate.id, templateData);
      } else {
        onCreate(templateData);
      }
      
      onClose();
    };

    const predefinedCategories = ['Medical', 'Bug Out', 'Vehicle', 'Home', 'Food & Water', 'Tools', 'Communication', 'Custom'];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-card rounded-lg max-w-4xl w-full my-8">
          <div className="bg-primary text-primary-foreground p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">{editingTemplate ? 'Edit Template' : 'Create Template'}</h2>
                <p className="text-foreground/80 mt-1">Build a template from your existing kits</p>
              </div>
              <button
                onClick={onClose}
                className="text-primary-foreground hover:bg-white/20 rounded-full p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {/* Organization Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-text-primary mb-3">Organization</h3>
              
              {!showNewOrg && existingOrgs.length > 0 ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Select Existing Organization
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.org_id}
                        onChange={(e) => {
                          const org = existingOrgs.find(o => o.id === e.target.value);
                          if (org) {
                            setFormData({
                              ...formData,
                              org_id: org.id,
                              org_name: org.name,
                              org_description: org.description,
                              org_website: org.website
                            });
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Choose an organization...</option>
                        {existingOrgs.map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                      {formData.org_id && (
                        <button
                          type="button"
                          onClick={() => {
                            const org = existingOrgs.find(o => o.id === formData.org_id);
                            if (org) {
                              setEditingOrganization(org);
                              setShowEditOrganization(true);
                            }
                          }}
                          className="px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors flex items-center gap-1"
                          title="Edit this organization"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNewOrg(true)}
                    className="text-primary hover:opacity-90 text-sm"
                  >
                    + Create New Organization
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        Organization Name *
                      </label>
                      <input
                        type="text"
                        value={formData.org_name}
                        onChange={(e) => handleOrgNameChange(e.target.value)}
                        placeholder="Wahle-Tec"
                        className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                      />
                      <p className="text-xs text-text-muted mt-1">Enter a descriptive name for your organization</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        Organization ID * <span className="text-text-muted font-normal">(Auto-generated, editable)</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.org_id}
                          onChange={(e) => setFormData({ ...formData, org_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                          placeholder="wahle_tec"
                          className="flex-1 px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, org_id: generateOrgId(formData.org_name) })}
                          className="px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors text-sm font-medium"
                          title="Regenerate ID from organization name"
                        >
                          🔄
                        </button>
                      </div>
                      <p className="text-xs text-text-muted mt-1">Lowercase, underscore separated (auto-filled)</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Organization Description
                    </label>
                    <input
                      type="text"
                      value={formData.org_description}
                      onChange={(e) => setFormData({ ...formData, org_description: e.target.value })}
                      placeholder="Curated from real-world experience"
                      className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Organization Website
                    </label>
                    <input
                      type="url"
                      value={formData.org_website}
                      onChange={(e) => setFormData({ ...formData, org_website: e.target.value })}
                      placeholder="https://wahle-tec.com"
                      className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  {existingOrgs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowNewOrg(false)}
                      className="text-primary hover:opacity-90 text-sm"
                    >
                      ← Use Existing Organization
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Template Details */}
            <div className="mb-6 border-t pt-6">
              <h3 className="text-lg font-semibold text-text-primary mb-3">Template Details</h3>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.template_name}
                    onChange={(e) => handleTemplateNameChange(e.target.value)}
                    placeholder="Individual First Aid Kit (IFAK)"
                    className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-xs text-text-muted mt-1">Enter a descriptive name for your template</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Template ID * <span className="text-text-muted font-normal">(Auto-generated, editable)</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.template_id}
                      onChange={(e) => setFormData({ ...formData, template_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                      placeholder="ifak"
                      className="flex-1 px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, template_id: generateTemplateId(formData.template_name) })}
                      className="px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors text-sm font-medium"
                      title="Regenerate ID from template name"
                    >
                      🔄
                    </button>
                  </div>
                  <p className="text-xs text-text-muted mt-1">Lowercase, underscore separated (auto-filled)</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Icon
                  </label>
                  <div className="relative flex gap-2">
                    <div className="flex-1 px-4 py-2 border border-input rounded-lg bg-surface-muted text-3xl text-center">
                      {formData.icon}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowEmojiPickerTemplate(!showEmojiPickerTemplate)}
                      className="px-4 py-2 border border-input rounded-lg hover:bg-surface-muted flex items-center gap-2"
                    >
                      <Smile className="w-5 h-5" />
                      Pick
                    </button>
                    {showEmojiPickerTemplate && (
                      <>
                        <div 
                          className="fixed inset-0 z-40" 
                          onClick={() => setShowEmojiPickerTemplate(false)}
                        />
                        <div className="absolute top-12 right-0 z-50 shadow-lg rounded-lg border border-border bg-card">
                          <EmojiPicker
                            onEmojiClick={(emoji) => {
                              setFormData({ ...formData, icon: emoji.emoji });
                              setShowEmojiPickerTemplate(false);
                            }}
                            autoFocusSearch={false}
                            theme="light"
                            width={300}
                            height={400}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Category
                  </label>
                  {!showCustomCategory ? (
                    <select
                      value={formData.category}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setShowCustomCategory(true);
                        } else {
                          setFormData({ ...formData, category: e.target.value });
                        }
                      }}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                    >
                      {predefinedCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                      <option value="__custom__">+ Add Custom Category</option>
                    </select>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customCategoryInput}
                        onChange={(e) => setCustomCategoryInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && customCategoryInput.trim()) {
                            setFormData({ ...formData, category: customCategoryInput.trim() });
                            setShowCustomCategory(false);
                            setCustomCategoryInput('');
                          }
                        }}
                        placeholder="Enter custom category"
                        className="flex-1 px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (customCategoryInput.trim()) {
                            setFormData({ ...formData, category: customCategoryInput.trim() });
                            setShowCustomCategory(false);
                            setCustomCategoryInput('');
                          }
                        }}
                        className="px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCustomCategory(false);
                          setCustomCategoryInput('');
                        }}
                        className="px-3 py-2 bg-gray-200 text-text-primary rounded-lg hover:bg-gray-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Template Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Overall instructions and description about this template..."
                  rows={3}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Tiers Section */}
            <div className="mb-6 border-t pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-text-primary">Tiers</h3>
                <div className="text-sm text-foreground/90">
                  Select kits to include as tiers
                </div>
              </div>

              {/* Kit Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Add Kit as Tier
                </label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleKitSelection(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value="">Choose a kit...</option>
                  {kits.filter(k => !tierConfigs.some(tc => tc.kitId === k.id)).map(kit => (
                    <option key={kit.id} value={kit.id}>
                      {kit.icon} {kit.name} ({kit.items?.length || 0} items)
                    </option>
                  ))}
                </select>
              </div>

              {/* Tier List */}
              {tierConfigs.length === 0 ? (
                <div className="text-center py-8 bg-surface-muted rounded-lg border-2 border-dashed border-input">
                  <Package className="w-12 h-12 text-text-muted mx-auto mb-2" />
                  <p className="text-foreground/90">No tiers added yet</p>
                  <p className="text-sm text-text-muted">Select kits above to add as tiers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tierConfigs.map((tier, index) => (
                    <div key={index} className="border border-input rounded-lg p-4 bg-surface-muted">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-text-primary mb-1">
                              Order
                            </label>
                            <input
                              type="number"
                              value={tier.tierOrder}
                              onChange={(e) => handleTierChange(index, 'tierOrder', parseInt(e.target.value))}
                              min="1"
                              className="w-full px-2 py-1 text-sm border border-input rounded focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-text-primary mb-1">
                              Tier Name
                            </label>
                            <input
                              type="text"
                              value={tier.tierName}
                              onChange={(e) => handleTierChange(index, 'tierName', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-input rounded focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveTier(index)}
                          className="ml-3 text-red-600 hover:bg-red-50 p-2 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-text-primary mb-1">
                          Tier Description
                        </label>
                        <input
                          type="text"
                          value={tier.tierDescription}
                          onChange={(e) => handleTierChange(index, 'tierDescription', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-input rounded focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div className="text-xs text-foreground/90 bg-card p-2 rounded border border-border">
                        <strong>{tier.items?.length || 0} items</strong> included in this tier
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t p-6 bg-surface-muted flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-foreground/90 hover:bg-surface-muted rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
            >
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ToolLayout
      title="Emergency Supply Inventory"
      description="Track your emergency kits and supplies"
      icon={Package}
      guide={{
        intro: 'Track everything in your emergency kits so nothing expires or goes missing.',
        steps: [
          'Create kits from preset templates or from scratch.',
          'Add items with quantity, cost, and expiration date.',
          'Switch to master view to see all items across kits.',
          'Use the audit checklist to verify your supplies.',
          'Export or print for your binder.',
        ],
      }}
    >
      <><div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-end mb-4">
            <div className="flex gap-2">
              <label
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center gap-2 cursor-pointer shadow-sm"
                title="Import kits and templates from file"
              >
                <FileUp className="w-4 h-4" />
                Import
                <input type="file" accept=".json" onChange={importData} className="hidden" />
              </label>
              <button
                onClick={exportData}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center gap-2 shadow-sm"
                title={`Export all data (${kits.length} kits, ${customTemplates.length} templates)`}
              >
                <FileDown className="w-4 h-4" />
                Export All
              </button>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex gap-2 bg-surface-muted p-1 rounded-lg inline-flex shadow-sm">
            <button
              onClick={() => setView('kits')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                view === 'kits' 
                  ? 'bg-primary text-primary-foreground shadow-md' 
                  : 'text-foreground/90 hover:bg-surface-muted'
              }`}
            >
              Kit View
            </button>
            <button
              onClick={() => setView('templates')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                view === 'templates' 
                  ? 'bg-primary text-primary-foreground shadow-md' 
                  : 'text-foreground/90 hover:bg-surface-muted'
              }`}
            >
              Templates
            </button>
            <button
              onClick={() => setView('master')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                view === 'master' 
                  ? 'bg-primary text-primary-foreground shadow-md' 
                  : 'text-foreground/90 hover:bg-surface-muted'
              }`}
            >
              Master Inventory
            </button>
            <button
              onClick={() => setView('tasks')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                view === 'tasks' 
                  ? 'bg-primary text-primary-foreground shadow-md' 
                  : 'text-foreground/90 hover:bg-surface-muted'
              }`}
            >
              Tasks & Shopping
            </button>
          </div>
        </div>

        {/* Dashboard Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
          <div 
            onClick={() => {
              setView('kits');
              setSelectedKit(null);
            }}
            className="bg-card p-4 rounded-lg shadow-sm border border-border cursor-pointer hover:shadow-md hover:border-primary transition-all"
          >
            <div className="text-sm text-foreground/90 mb-1">Total Kits</div>
            <div className="text-2xl font-bold text-primary">{stats.totalKits}</div>
          </div>
          <div 
            onClick={() => setView('master')}
            className="bg-card p-4 rounded-lg shadow-sm border border-border cursor-pointer hover:shadow-md hover:border-border transition-all"
          >
            <div className="text-sm text-foreground/90 mb-1">Total Items</div>
            <div className="text-2xl font-bold text-text-primary">{stats.totalItems}</div>
          </div>
          <div 
            onClick={() => stats.expiredItems > 0 && setDrillDownView('expired')}
            className={`bg-card p-4 rounded-lg shadow-sm border border-border ${
              stats.expiredItems > 0 ? 'cursor-pointer hover:shadow-md hover:border-red-300 transition-all' : ''
            }`}
          >
            <div className="text-sm text-foreground/90 mb-1">Expired</div>
            <div className="text-2xl font-bold text-red-600">{stats.expiredItems}</div>
          </div>
          <div 
            onClick={() => stats.expiringSoon > 0 && setDrillDownView('expiring')}
            className={`bg-card p-4 rounded-lg shadow-sm border border-border ${
              stats.expiringSoon > 0 ? 'cursor-pointer hover:shadow-md hover:border-orange-300 transition-all' : ''
            }`}
          >
            <div className="text-sm text-foreground/90 mb-1">Expiring Soon</div>
            <div className="text-2xl font-bold text-orange-600">{stats.expiringSoon}</div>
          </div>
          <div 
            onClick={() => stats.lowStock > 0 && setDrillDownView('lowStock')}
            className={`bg-card p-4 rounded-lg shadow-sm border border-border ${
              stats.lowStock > 0 ? 'cursor-pointer hover:shadow-md hover:border-primary transition-all' : ''
            }`}
          >
            <div className="text-sm text-foreground/90 mb-1">Low Stock</div>
            <div className="text-2xl font-bold text-primary">{stats.lowStock}</div>
          </div>
          <div 
            onClick={() => stats.maintenanceOverdue > 0 && setDrillDownView('maintenanceOverdue')}
            className={`bg-card p-4 rounded-lg shadow-sm border border-border ${
              stats.maintenanceOverdue > 0 ? 'cursor-pointer hover:shadow-md hover:border-red-300 transition-all' : ''
            }`}
          >
            <div className="text-sm text-foreground/90 mb-1">Maint. Overdue</div>
            <div className="text-2xl font-bold text-red-600">{stats.maintenanceOverdue}</div>
          </div>
          <div 
            onClick={() => stats.maintenanceDueSoon > 0 && setDrillDownView('maintenanceDue')}
            className={`bg-card p-4 rounded-lg shadow-sm border border-border ${
              stats.maintenanceDueSoon > 0 ? 'cursor-pointer hover:shadow-md hover:border-orange-300 transition-all' : ''
            }`}
          >
            <div className="text-sm text-foreground/90 mb-1">Maint. Due</div>
            <div className="text-2xl font-bold text-orange-600">{stats.maintenanceDueSoon}</div>
          </div>
          <div className="bg-card p-4 rounded-lg shadow-sm border border-border">
            <div className="text-sm text-foreground/90 mb-1">Total Value</div>
            <div className="text-2xl font-bold text-green-600">${stats.totalValue.toFixed(0)}</div>
          </div>
        </div>

        {/* Kit View */}
        {view === 'kits' && !selectedKit && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-text-primary">Your Kits</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCleanupKits}
                  className="px-3 py-2 bg-gray-100 text-text-primary rounded-lg hover:bg-surface-muted flex items-center gap-2 text-sm"
                  title="Fix broken kit hierarchies"
                >
                  <AlertCircle className="w-4 h-4" />
                  Fix Issues
                </button>
                <button
                  onClick={() => setShowAddKit(true)}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Kit
                </button>
              </div>
            </div>

            {kits.length === 0 ? (
              <div className="bg-card p-12 rounded-lg shadow-sm text-center">
                <Package className="w-16 h-16 text-text-muted mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-text-primary mb-2">No kits yet</h3>
                <p className="text-foreground/90 mb-4">Start by creating your first emergency kit</p>
                <button
                  onClick={() => setShowAddKit(true)}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                >
                  Create First Kit
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {kitTree.map(kit => renderKitTree(kit, 0))}
              </div>
            )}
          </div>
        )}

        {/* Kit Detail View */}
        {view === 'kits' && selectedKit && (
          <div>
            <div className="mb-6">
              {/* Breadcrumb Navigation */}
              <div className="flex items-center gap-2 mb-4 text-sm">
                <button
                  onClick={() => setSelectedKit(null)}
                  className="text-primary hover:opacity-90"
                >
                  All Kits
                </button>
                {getKitBreadcrumb(selectedKit.id).map((breadcrumbKit, index, array) => (
                  <React.Fragment key={breadcrumbKit.id}>
                    <ChevronRight className="w-4 h-4 text-text-muted" />
                    <button
                      onClick={() => index < array.length - 1 ? setSelectedKit(breadcrumbKit) : null}
                      className={index < array.length - 1 ? "text-primary hover:opacity-90" : "text-text-primary font-semibold"}
                    >
                      {breadcrumbKit.icon} {breadcrumbKit.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
              
              <div className={`bg-card p-6 rounded-lg shadow-md border ${
                selectedKit.is_general_stock ? 'border-blue-300 bg-blue-50' : 'border-border'
              }`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="text-5xl">{selectedKit.icon}</div>
                    <div>
                      <h2 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                        {selectedKit.name}
                        {selectedKit.is_general_stock && (
                          <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded">Unassigned Items</span>
                        )}
                      </h2>
                      <p className="text-foreground/90">{selectedKit.description}</p>
                      {selectedKit.location && (
                        <div className="flex items-center gap-2 text-foreground/90 mt-2">
                          <MapPin className="w-4 h-4" />
                          {selectedKit.location}
                        </div>
                      )}
                      {selectedKit.parent_kit_id && (
                        <div className="flex items-center gap-2 text-foreground/90 mt-2">
                          <Package className="w-4 h-4" />
                          <span>Subkit of: {kits.find(k => k.id === selectedKit.parent_kit_id)?.icon} {kits.find(k => k.id === selectedKit.parent_kit_id)?.name || 'Unknown'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowAddItem(true)}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                    <button
                      onClick={handleCompleteAudit}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <CheckSquare className="w-4 h-4" />
                      Complete Audit
                    </button>
                    <button
                      onClick={() => handlePrint('kit')}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Printer className="w-4 h-4" />
                      Print Checklist
                    </button>
                    {selectedKit.items.filter(item => item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold).length > 0 && (
                      <button
                        onClick={() => handlePrint('kit-shopping')}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Print Shopping List ({selectedKit.items.filter(item => item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold).length})
                      </button>
                    )}
                  </div>
                </div>

                {selectedKit.last_audit_date && (
                  <div className="text-sm text-foreground/90">
                    Last audited: {new Date(selectedKit.last_audit_date).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedItems.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="font-semibold text-blue-900">
                      {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
                    </span>
                    <button
                      onClick={() => setSelectedItems([])}
                      className="text-sm text-blue-700 hover:text-blue-900"
                    >
                      Clear selection
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {!showBulkActions ? (
                      <button
                        onClick={() => setShowBulkActions(true)}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                      >
                        Bulk Actions
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <select
                          value={bulkAction}
                          onChange={(e) => setBulkAction(e.target.value)}
                          className="px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Select action...</option>
                          <option value="move">Move to another kit</option>
                          <option value="copy">Copy to another kit</option>
                          <option value="delete">Delete items</option>
                          <option value="category">Change category</option>
                          <option value="location">Change location</option>
                        </select>

                        {bulkAction === 'category' && (
                          <select
                            value={bulkCategory}
                            onChange={(e) => setBulkCategory(e.target.value)}
                            className="px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                          >
                            <option value="">Select category...</option>
                            {categories.map(cat => (
                              <option key={cat.name} value={cat.name}>{cat.name}</option>
                            ))}
                          </select>
                        )}

                        {bulkAction === 'location' && (
                          <input
                            type="text"
                            value={bulkLocation}
                            onChange={(e) => setBulkLocation(e.target.value)}
                            placeholder="New location..."
                            className="px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        )}

                        <button
                          onClick={handleBulkAction}
                          disabled={!bulkAction || (bulkAction === 'category' && !bulkCategory)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Apply
                        </button>
                        <button
                          onClick={() => {
                            setShowBulkActions(false);
                            setBulkAction('');
                            setBulkCategory('');
                            setBulkLocation('');
                          }}
                          className="px-4 py-2 bg-gray-200 text-text-primary rounded-lg hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Kit Items */}
            <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-muted border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">
                        <input
                          type="checkbox"
                          checked={selectedItems.length === selectedKit.items.length && selectedKit.items.length > 0}
                          onChange={handleSelectAllItems}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-primary"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">✓</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Item</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Expiration</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Link</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {selectedKit.items.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-foreground/90">
                          No items in this kit yet. Click "Add Item" to get started.
                        </td>
                      </tr>
                    ) : (
                      selectedKit.items.map(item => {
                        const expirationStatus = getExpirationStatus(item.expiration_date);
                        const isLowStock = item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold;
                        const maintenanceStatus = getMaintenanceStatus(item);
                        
                        return (
                          <tr key={item.id} className={`hover:bg-surface-muted ${selectedItems.includes(item.id) ? 'bg-blue-50' : ''}`}>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(item.id)}
                                onChange={() => handleToggleSelectItem(item.id)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-primary"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleToggleItemCheck(item.id)}
                                className="text-text-muted hover:text-blue-600"
                              >
                                {item.is_checked ? (
                                  <CheckSquare className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <Square className="w-5 h-5" />
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="font-medium text-text-primary">{item.name}</div>
                                {maintenanceStatus === 'overdue' && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full whitespace-nowrap">
                                    Maintenance Overdue
                                  </span>
                                )}
                                {maintenanceStatus === 'due-soon' && (
                                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full whitespace-nowrap">
                                    Maintenance Due
                                  </span>
                                )}
                              </div>
                              {item.notes && (
                                <div className="text-sm text-foreground/90">{item.notes}</div>
                              )}
                              {item.needs_maintenance && item.next_maintenance_date && (
                                <div className="text-xs text-text-muted mt-1">
                                  Next {item.maintenance_type}: {new Date(item.next_maintenance_date).toLocaleDateString()}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground/90">
                              {item.category}
                              {item.subcategory && ` - ${item.subcategory}`}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${isLowStock ? 'text-orange-600' : 'text-text-primary'}`}>
                                  {item.quantity} {item.unit}
                                </span>
                                {isLowStock && (
                                  <AlertCircle className="w-4 h-4 text-orange-600" />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {item.expiration_date ? (
                                <div>
                                  {new Date(item.expiration_date).toLocaleDateString()}
                                </div>
                              ) : (
                                <span className="text-text-muted">No expiration</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {expirationStatus === 'expired' && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Expired</span>
                              )}
                              {expirationStatus === 'expiring-soon' && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">Expiring Soon</span>
                              )}
                              {expirationStatus === 'warning' && (
                                <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">Check Soon</span>
                              )}
                              {expirationStatus === 'good' && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Good</span>
                              )}
                              {expirationStatus === 'none' && (
                                <span className="px-2 py-1 bg-gray-100 text-foreground/90 text-xs rounded-full">N/A</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {item.product_url ? (
                                <a
                                  href={item.product_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                                  title="View product online"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              ) : (
                                <span className="text-text-muted">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                {item.needs_maintenance && (maintenanceStatus === 'overdue' || maintenanceStatus === 'due-soon') && (
                                  <button
                                    onClick={() => handleCompleteMaintenance(item.id)}
                                    className="text-green-600 hover:text-green-700"
                                    title="Mark maintenance complete"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleOpenTransferModal(item, 'move')}
                                  className="text-purple-600 hover:text-purple-700"
                                  title="Move to another kit"
                                >
                                  <Archive className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleOpenTransferModal(item, 'copy')}
                                  className="text-teal-600 hover:text-teal-700"
                                  title="Copy to another kit"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditingItem({
                                    ...item,
                                    maintenance_schedules: item.maintenance_schedules ? 
                                      item.maintenance_schedules.map(schedule => ({...schedule})) : []
                                  })}
                                  className="text-primary hover:opacity-90"
                                  title="Edit item"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Delete item"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tasks & Shopping View */}
        {view === 'tasks' && (
          <div>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-text-primary">Tasks & Shopping</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePrint('tasks')}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print Action List
                  </button>
                </div>
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setTaskFilter('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    taskFilter === 'all'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-card text-text-primary border border-input hover:bg-surface-muted'
                  }`}
                >
                  All Tasks ({shoppingList.length + maintenanceTasks.needsPurchase.length + maintenanceTasks.noPurchase.length})
                </button>
                <button
                  onClick={() => setTaskFilter('shopping')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    taskFilter === 'shopping'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-card text-text-primary border border-input hover:bg-surface-muted'
                  }`}
                >
                  Shopping Only ({shoppingList.length + maintenanceTasks.needsPurchase.length})
                </button>
                <button
                  onClick={() => setTaskFilter('maintenance')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    taskFilter === 'maintenance'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-card text-text-primary border border-input hover:bg-surface-muted'
                  }`}
                >
                  Maintenance Only ({maintenanceTasks.needsPurchase.length + maintenanceTasks.noPurchase.length})
                </button>
                <button
                  onClick={() => setTaskFilter('week')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    taskFilter === 'week'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-card text-text-primary border border-input hover:bg-surface-muted'
                  }`}
                >
                  This Week ({maintenanceTasks.thisWeek.length})
                </button>
                <button
                  onClick={() => setTaskFilter('month')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    taskFilter === 'month'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-card text-text-primary border border-input hover:bg-surface-muted'
                  }`}
                >
                  This Month ({maintenanceTasks.thisMonth.length})
                </button>
              </div>

              {/* Cost Summary */}
              {(taskFilter === 'all' || taskFilter === 'shopping') && (
                <div className="bg-primary/10 border border-border rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-text-primary mb-1">Estimated Shopping Cost</h3>
                      <div className="flex items-center gap-4 text-sm text-foreground/90">
                        <span>Restock: ${taskCosts.restockCost.toFixed(2)}</span>
                        <span>Maintenance: ${taskCosts.maintenanceCost.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-primary">${taskCosts.total.toFixed(2)}</div>
                      <div className="text-xs text-foreground/90">Total</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Low Stock Items */}
            {(taskFilter === 'all' || taskFilter === 'shopping') && shoppingList.length > 0 && (
              <div className="mb-8">
                <div className="bg-primary/10 border border-border rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-text-primary flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-primary" />
                    Low Stock - Needs Restocking ({shoppingList.length} items)
                  </h3>
                  <p className="text-sm text-foreground/90 mt-1">
                    These items have fallen below their minimum stock levels
                  </p>
                </div>
                <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-surface-muted border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Kit</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Current</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Need</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">To Buy</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Cost</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {shoppingList.map(item => {
                        const toBuy = Math.max(item.low_stock_threshold - item.quantity, 1);
                        return (
                          <tr key={item.id} className="hover:bg-surface-muted">
                            <td className="px-4 py-3 font-medium">{item.name}</td>
                            <td className="px-4 py-3 text-sm">{item.kitIcon} {item.kitName}</td>
                            <td className="px-4 py-3 text-sm text-orange-600 font-semibold">{item.quantity} {item.unit}</td>
                            <td className="px-4 py-3 text-sm">{item.low_stock_threshold} {item.unit}</td>
                            <td className="px-4 py-3 text-sm font-bold">{toBuy} {item.unit}</td>
                            <td className="px-4 py-3 text-sm">${(item.cost * toBuy).toFixed(2)}</td>
                            <td className="px-4 py-3 text-center">
                              {item.product_url ? (
                                <a
                                  href={item.product_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                                  title="View product online"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              ) : (
                                <span className="text-text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Maintenance Requiring Purchase */}
            {(taskFilter === 'all' || taskFilter === 'shopping' || taskFilter === 'maintenance' || taskFilter === 'week' || taskFilter === 'month') && maintenanceTasks.needsPurchase.length > 0 && (
              <div className="mb-8">
                <div className="bg-purple-50 border border-purple-300 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    <AlertCircle className="w-5 h-5" />
                    Maintenance - Requires Purchase ({maintenanceTasks.needsPurchase.length} items)
                  </h3>
                  <p className="text-sm text-purple-800 mt-1">
                    These maintenance tasks require buying replacements (Replace/Rotate)
                  </p>
                </div>
                <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-surface-muted border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Kit</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Notes</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Task</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Due Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Cost</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Link</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {maintenanceTasks.needsPurchase.filter(schedule => {
                        if (taskFilter === 'week') {
                          return maintenanceTasks.thisWeek.some(weekSchedule => weekSchedule.id === schedule.id);
                        }
                        if (taskFilter === 'month') {
                          return maintenanceTasks.thisMonth.some(monthSchedule => monthSchedule.id === schedule.id);
                        }
                        return true;
                      }).map(schedule => {
                        const status = getMaintenanceStatus(schedule);
                        const daysUntil = schedule.next_due 
                          ? Math.ceil((new Date(schedule.next_due) - new Date()) / (1000 * 60 * 60 * 24))
                          : 0;
                        return (
                          <tr key={schedule.id} className="hover:bg-surface-muted">
                            <td className="px-4 py-3 font-medium">{schedule.itemName}</td>
                            <td className="px-4 py-3 text-sm">{schedule.kitIcon} {schedule.kitName}</td>
                            <td className="px-4 py-3 text-sm text-foreground/90 italic">
                              {schedule.notes || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm capitalize">{schedule.type}</td>
                            <td className="px-4 py-3 text-sm">
                              {schedule.next_due ? new Date(schedule.next_due).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-3">
                              {status === 'overdue' && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                                  {Math.abs(daysUntil)} days overdue
                                </span>
                              )}
                              {status === 'due-soon' && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                                  Due in {daysUntil} days
                                </span>
                              )}
                              {status === 'upcoming' && (
                                <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                                  In {daysUntil} days
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">${(schedule.itemCost || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-center">
                              {schedule.itemProductUrl ? (
                                <a
                                  href={schedule.itemProductUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                                  title="View product online"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              ) : (
                                <span className="text-text-muted">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleLogMaintenance(schedule.itemId, schedule.kitId, schedule.id)}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
                              >
                                <Check className="w-3 h-3" />
                                Log Done
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Maintenance Not Requiring Purchase */}
            {(taskFilter === 'all' || taskFilter === 'maintenance' || taskFilter === 'week' || taskFilter === 'month') && maintenanceTasks.noPurchase.length > 0 && (
              <div className="mb-8">
                <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-blue-900 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Maintenance - No Purchase Needed ({maintenanceTasks.noPurchase.length} items)
                  </h3>
                  <p className="text-sm text-blue-800 mt-1">
                    These maintenance tasks don't require shopping (Test/Inspect)
                  </p>
                </div>
                <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-surface-muted border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Kit</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Notes</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Due Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Last Done</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {maintenanceTasks.noPurchase.filter(schedule => {
                        if (taskFilter === 'week') {
                          return maintenanceTasks.thisWeek.some(weekSchedule => weekSchedule.id === schedule.id);
                        }
                        if (taskFilter === 'month') {
                          return maintenanceTasks.thisMonth.some(monthSchedule => monthSchedule.id === schedule.id);
                        }
                        return true;
                      }).map(schedule => {
                        const status = getMaintenanceStatus(schedule);
                        const daysUntil = schedule.next_due 
                          ? Math.ceil((new Date(schedule.next_due) - new Date()) / (1000 * 60 * 60 * 24))
                          : 0;
                        return (
                          <tr key={schedule.id} className="hover:bg-surface-muted">
                            <td className="px-4 py-3 font-medium">{schedule.itemName}</td>
                            <td className="px-4 py-3 text-sm">{schedule.kitIcon} {schedule.kitName}</td>
                            <td className="px-4 py-3 text-sm text-foreground/90 italic">
                              {schedule.notes || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm capitalize">{schedule.type}</td>
                            <td className="px-4 py-3 text-sm">
                              {schedule.next_due ? new Date(schedule.next_due).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-3">
                              {status === 'overdue' && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                                  {Math.abs(daysUntil)} days overdue
                                </span>
                              )}
                              {status === 'due-soon' && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                                  Due in {daysUntil} days
                                </span>
                              )}
                              {status === 'upcoming' && (
                                <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                                  In {daysUntil} days
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {schedule.last_done ? new Date(schedule.last_done).toLocaleDateString() : 'Never'}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleLogMaintenance(schedule.itemId, schedule.kitId, schedule.id)}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
                              >
                                <Check className="w-3 h-3" />
                                Log Done
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty States */}
            {shoppingList.length === 0 && maintenanceTasks.needsPurchase.length === 0 && maintenanceTasks.noPurchase.length === 0 && (
              <div className="bg-card p-12 rounded-lg shadow-sm text-center">
                <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-text-primary mb-2">All Caught Up!</h3>
                <p className="text-foreground/90">No tasks or shopping needed right now. Great job keeping your emergency supplies maintained!</p>
              </div>
            )}
          </div>
        )}

        {/* Templates View */}
        {view === 'templates' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-text-primary">Template Manager</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowImportTemplate(true)}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center gap-2"
                >
                  <FileUp className="w-4 h-4" />
                  Import Template
                </button>
                <button
                  onClick={() => setShowCreateTemplate(true)}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Template
                </button>
              </div>
            </div>

            {customTemplates.length === 0 ? (
              <div className="bg-card p-12 rounded-lg shadow-sm text-center">
                <Package className="w-16 h-16 text-text-muted mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-text-primary mb-2">No custom templates yet</h3>
                <p className="text-foreground/90 mb-4">Create templates from your kits or import templates from others</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setShowCreateTemplate(true)}
                    className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                  >
                    Create Template
                  </button>
                  <button
                    onClick={() => setShowImportTemplate(true)}
                    className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                  >
                    Import Template
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Group templates by organization */}
                {Object.entries(
                  customTemplates.reduce((acc, template) => {
                    const orgId = template.org_id || 'uncategorized';
                    if (!acc[orgId]) {
                      acc[orgId] = {
                        name: template.org_name || 'Uncategorized',
                        templates: []
                      };
                    }
                    acc[orgId].templates.push(template);
                    return acc;
                  }, {})
                ).map(([orgId, { name: orgName, templates: orgTemplates }]) => (
                  <div key={orgId} className="bg-card rounded-lg shadow-sm border border-border">
                    {/* Organization Header */}
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-border">
                      <div 
                        className="flex items-center gap-3 flex-1 cursor-pointer hover:bg-gray-100 -m-4 p-4"
                        onClick={() => {
                          const newCollapsed = new Set(collapsedTemplateOrgs);
                          if (newCollapsed.has(orgId)) {
                            newCollapsed.delete(orgId);
                          } else {
                            newCollapsed.add(orgId);
                          }
                          setCollapsedTemplateOrgs(newCollapsed);
                        }}
                      >
                        {collapsedTemplateOrgs.has(orgId) ? (
                          <ChevronRight className="w-5 h-5 text-text-muted" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-text-muted" />
                        )}
                        <h3 className="text-lg font-bold text-text-primary">{orgName}</h3>
                        <span className="text-sm text-text-muted">({orgTemplates.length} template{orgTemplates.length !== 1 ? 's' : ''})</span>
                      </div>
                      
                      {/* Edit Organization Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Get full org data from the first template
                          const orgData = {
                            org_id: orgId,
                            org_name: orgName,
                            org_description: orgTemplates[0]?.org_description || '',
                            org_website: orgTemplates[0]?.org_website || ''
                          };
                          setEditingOrganization(orgData);
                          setShowEditOrganization(true);
                        }}
                        className="px-3 py-2 text-purple-600 hover:bg-purple-50 rounded-lg flex items-center gap-1 text-sm"
                        title="Edit Organization"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit Org
                      </button>
                    </div>

                    {/* Templates List */}
                    {!collapsedTemplateOrgs.has(orgId) && (
                      <div className="divide-y divide-gray-200">
                        {orgTemplates.map(template => (
                          <div key={template.id} className="p-4 hover:bg-surface-muted">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <div className="text-3xl">{template.icon}</div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900">{template.template_name}</h4>
                                  <p className="text-sm text-foreground/90 mt-1">{template.description || 'No description'}</p>
                                  <div className="flex items-center gap-3 mt-2 text-sm text-text-muted">
                                    <span className="flex items-center gap-1">
                                      <Package className="w-4 h-4" />
                                      {template.tiers?.length || 0} tier{(template.tiers?.length || 0) !== 1 ? 's' : ''}
                                    </span>
                                    <span>•</span>
                                    <span>{template.category}</span>
                                    {template.is_imported && (
                                      <>
                                        <span>•</span>
                                        <span className="text-green-600">Imported</span>
                                      </>
                                    )}
                                  </div>
                                  
                                  {/* Tier Summary */}
                                  {template.tiers && template.tiers.length > 0 && (
                                    <div className="mt-3 space-y-1">
                                      {template.tiers.map((tier, idx) => (
                                        <div key={idx} className="text-xs text-text-muted pl-4 border-l-2 border-input">
                                          <span className="font-medium">{tier.name}</span> - {tier.items?.length || 0} items
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Actions */}
                              <div className="flex items-center gap-2 ml-4">
                                <button
                                  onClick={() => {
                                    setEditingTemplate(template);
                                    setShowCreateTemplate(true);
                                  }}
                                  className="px-3 py-2 text-foreground/90 hover:bg-gray-100 rounded-lg flex items-center gap-1 text-sm"
                                  title="Edit Template"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => syncTemplateWithKits(template.id)}
                                  className="px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg flex items-center gap-1 text-sm"
                                  title="Update template with latest kit data"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                  Sync
                                </button>
                                <button
                                  onClick={() => exportTemplateAsJSON(template)}
                                  className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1 text-sm"
                                  title="Export as JSON"
                                >
                                  <FileDown className="w-4 h-4" />
                                  JSON
                                </button>
                                <button
                                  onClick={() => exportTemplateAsPython(template)}
                                  className="px-3 py-2 text-purple-600 hover:bg-purple-50 rounded-lg flex items-center gap-1 text-sm"
                                  title="Export as Python"
                                >
                                  <FileDown className="w-4 h-4" />
                                  Python
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Delete template "${template.template_name}"?`)) {
                                      deleteTemplate(template.id);
                                    }
                                  }}
                                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1 text-sm"
                                  title="Delete Template"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Master Inventory View */}
        {view === 'master' && (
          <div>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-text-primary">Master Inventory</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePrint('master')}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print Report
                  </button>
                  {shoppingList.length > 0 && (
                    <button
                      onClick={() => handlePrint('shopping')}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Print Shopping List ({shoppingList.length})
                    </button>
                  )}
                  {(stats.maintenanceOverdue > 0 || stats.maintenanceDueSoon > 0) && (
                    <button
                      onClick={() => handlePrint('maintenance')}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center gap-2"
                    >
                      <Printer className="w-4 h-4" />
                      Maintenance Schedule ({stats.maintenanceOverdue + stats.maintenanceDueSoon})
                    </button>
                  )}
                </div>
              </div>
              
              {/* Filters */}
              <div className="bg-card p-4 rounded-lg shadow-sm border border-border mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search items..."
                        className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Category</label>
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="all">All Categories</option>
                      {allCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Expiration</label>
                    <select
                      value={filterExpiration}
                      onChange={(e) => setFilterExpiration(e.target.value)}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="all">All Items</option>
                      <option value="expired">Expired Only</option>
                      <option value="expiring">Expiring Soon</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Household Size
                      <span className="text-xs text-text-muted ml-1">(for recommendations)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={householdSize}
                      onChange={(e) => handleHouseholdSizeChange(parseInt(e.target.value, 10) || 1)}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Charts */}
              {categoryData.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="bg-card p-6 rounded-lg border border-border">
                    <h3 className="text-lg font-semibold text-text-primary mb-4">Items by Category</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'var(--surface)',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: 'var(--radius)',
                            color: 'var(--text-primary)',
                            padding: '10px 14px',
                          }}
                          labelStyle={{ color: 'var(--text-primary)' }}
                          itemStyle={{ color: 'var(--text-primary)' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-card p-6 rounded-lg border border-border">
                    <h3 className="text-lg font-semibold text-text-primary mb-4">Inventory Distribution</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={categoryData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'var(--surface)',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: 'var(--radius)',
                            color: 'var(--text-primary)',
                            padding: '10px 14px',
                          }}
                          labelStyle={{ color: 'var(--text-primary)' }}
                          itemStyle={{ color: 'var(--text-primary)' }}
                        />
                        <Bar dataKey="value" fill="hsl(var(--primary))" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Recommendation Legend */}
              <div className="bg-primary/10 border border-border rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <div className="font-semibold text-text-primary mb-2">
                      FEMA/Red Cross Household Standards
                      <span className="text-foreground/90 font-normal ml-2">
                        (Calculated for {householdSize} {householdSize === 1 ? 'person' : 'people'})
                      </span>
                    </div>
                    <p className="text-foreground/90 mb-2">
                      Recommendations for overall household emergency preparedness (not individual portable kits). 
                      Adjust household size in filters above to personalize recommendations.
                    </p>
                    <div className="flex flex-wrap gap-4 text-foreground/90">
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        <span>Meets Recommended</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Info className="w-4 h-4 text-primary" />
                        <span>Meets Minimum</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4 text-destructive" />
                        <span>Below Standard</span>
                      </div>
                      <span className="text-xs text-text-muted ml-2">Hover over icons for details</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items List */}
              <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-muted border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Kit</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Quantity</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Expiration</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/90 uppercase">Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredItems.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-4 py-8 text-center text-foreground/90">
                            No items found matching your filters.
                          </td>
                        </tr>
                      ) : (
                        filteredItems.map(item => {
                          const expirationStatus = getExpirationStatus(item.expiration_date);
                          const isLowStock = item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold;
                          const recommendation = checkRecommendation(item);
                          
                          return (
                            <tr key={item.id} className="hover:bg-surface-muted">
                              <td className="px-4 py-3">
                                <div className="font-medium text-text-primary">{item.name}</div>
                                {item.notes && (
                                  <div className="text-sm text-foreground/90">{item.notes}</div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span>{item.kitIcon}</span>
                                  <span className="text-sm text-foreground/90">{item.kitName}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground/90">
                                {item.category}
                                {item.subcategory && ` - ${item.subcategory}`}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium ${isLowStock ? 'text-orange-600' : 'text-text-primary'}`}>
                                    {item.quantity} {item.unit}
                                  </span>
                                  {isLowStock && (
                                    <AlertCircle className="w-4 h-4 text-orange-600" />
                                  )}
                                  {recommendation && (
                                    <div className="group relative">
                                      {recommendation.status === 'meets' && (
                                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                                      )}
                                      {recommendation.status === 'minimum' && (
                                        <Info className="w-4 h-4 text-primary" />
                                      )}
                                      {recommendation.status === 'below' && (
                                        <AlertTriangle className="w-4 h-4 text-red-600" />
                                      )}
                                      <div className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                                        <div className="font-semibold mb-1">
                                          {recommendation.source} Household Standard
                                        </div>
                                        {recommendation.isPerPerson && (
                                          <div className="text-blue-300 mb-2">
                                            For household of {householdSize} {householdSize === 1 ? 'person' : 'people'}
                                          </div>
                                        )}
                                        <div className="mb-1">
                                          Minimum: {recommendation.minimum} {recommendation.unit}
                                        </div>
                                        <div className="mb-1">
                                          Recommended: {recommendation.recommended} {recommendation.unit}
                                        </div>
                                        {recommendation.note && (
                                          <div className="text-gray-300 italic mt-1">
                                            {recommendation.note}
                                          </div>
                                        )}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground/90">
                                {item.location || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {item.expiration_date ? (
                                  <div>
                                    {new Date(item.expiration_date).toLocaleDateString()}
                                  </div>
                                ) : (
                                  <span className="text-text-muted">No expiration</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {expirationStatus === 'expired' && (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Expired</span>
                                )}
                                {expirationStatus === 'expiring-soon' && (
                                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">Expiring Soon</span>
                                )}
                                {expirationStatus === 'warning' && (
                                  <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">Check Soon</span>
                                )}
                                {expirationStatus === 'good' && (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Good</span>
                                )}
                                {expirationStatus === 'none' && (
                                  <span className="px-2 py-1 bg-gray-100 text-foreground/90 text-xs rounded-full">N/A</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {item.product_url ? (
                                  <a
                                    href={item.product_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                                    title="View product online"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                ) : (
                                  <span className="text-text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Kit Modal */}
        {showAddKit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-text-primary">Create New Kit</h3>
                  <button onClick={() => setShowAddKit(false)} className="text-text-muted hover:text-foreground/90">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Templates by Source */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-text-primary mb-4">Start from Template</h4>
                  
                  {Object.entries(templatesBySource)
                    .sort(([sourceIdA], [sourceIdB]) => {
                      // Always show Wahle-Tec first (recommended by company)
                      if (sourceIdA === 'wahle_tec') return -1;
                      if (sourceIdB === 'wahle_tec') return 1;
                      // Otherwise maintain original order
                      return 0;
                    })
                    .map(([sourceId, sourceData]) => {
                    const sourceTemplates = Object.entries(sourceData.templates || {});
                    
                    return (
                      <div key={sourceId} className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          {sourceData.logo && (
                            <img 
                              src={sourceData.logo} 
                              alt={`${sourceData.name} logo`}
                              className={`h-6 w-auto object-contain ${sourceId === 'wahle_tec' ? 'invert' : ''}`}
                              onError={(e) => {
                                // Hide image if it fails to load
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <h5 className="text-sm font-semibold text-text-primary">{sourceData.name}</h5>
                          <span className="text-xs text-foreground/90">- {sourceData.description}</span>
                        </div>
                        
                        {sourceTemplates.length === 0 ? (
                          <div className="border border-dashed border-input rounded-lg p-6 bg-surface-muted">
                            <p className="text-sm text-text-muted italic text-center">
                              Templates coming soon from {sourceData.name}
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {sourceTemplates.map(([templateId, template]) => (
                              <div
                                key={`${sourceId}_${templateId}`}
                                className="border border-input rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                                onClick={async () => {
                                // Force refresh templates from API to ensure we have latest costs
                                try {
                                  // Use embedded templates
                                  const templatesData = loadTemplates();
                                  setTemplatesBySource(templatesData);
                                  
                                  // Get the template from embedded data
                                  const latestTemplate = templatesData[sourceId]?.templates?.[templateId] || template;
                                  setPreviewTemplateId(`${sourceId}_${templateId}`);
                                  setPreviewTemplate(latestTemplate);
                                
                                // Initialize items - select Base Kit by default (includes Minimal + Base)
                                const initialItems = {};
                                if (latestTemplate.tiers && Array.isArray(latestTemplate.tiers)) {
                                  // New tier-based format
                                  // Find Base Kit tier index (should be 1)
                                  const baseKitIndex = latestTemplate.tiers.findIndex(t => t.name === "Base Kit");
                                  const defaultTierIndex = baseKitIndex >= 0 ? baseKitIndex : 1;
                                  
                                  latestTemplate.tiers.forEach((tier, tierIndex) => {
                                    // Auto-select items up to and including Base Kit tier
                                    const autoSelect = tierIndex <= defaultTierIndex;
                                    
                                    // Safety check: ensure tier.items exists and is an array
                                    if (tier.items && Array.isArray(tier.items)) {
                                      tier.items.forEach((item, itemIndex) => {
                                        const itemKey = `${tierIndex}-${itemIndex}`;
                                        initialItems[itemKey] = {
                                          ...item,
                                          selected: autoSelect,
                                          quantity: autoSelect ? (item.quantity || 1) : 0
                                        };
                                      });
                                    }
                                  });
                                } else if (latestTemplate.items && Array.isArray(latestTemplate.items)) {
                                  // Old flat items format - convert to tier structure
                                  latestTemplate.items.forEach((item, itemIndex) => {
                                    const itemKey = `0-${itemIndex}`;
                                    initialItems[itemKey] = {
                                      ...item,
                                      selected: true,
                                      quantity: item.quantity || 1
                                    };
                                  });
                                }
                                setSelectedTemplateItems(initialItems);
                                setShowTemplatePreview(true);
                                } catch (error) {
                                  console.error('Error refreshing templates:', error);
                                  // Fallback to existing template if refresh fails
                                  setPreviewTemplateId(`${sourceId}_${templateId}`);
                                  setPreviewTemplate(template);
                                  setShowTemplatePreview(true);
                                }
                              }}
                              >
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="text-3xl">{template.icon}</div>
                                  <div>
                                    <h5 className="font-semibold text-text-primary">{template.name}</h5>
                                    <p className="text-sm text-foreground/90">{template.category}</p>
                                  </div>
                                </div>
                                <p className="text-sm text-foreground/90">{template.description}</p>
                                <p className="text-xs text-blue-600 mt-2">
                                  {template.tiers 
                                    ? template.tiers.reduce((sum, tier) => sum + (tier.items?.length || 0), 0)
                                    : (template.items?.length || 0)
                                  } preset items
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Custom Templates */}
                {customTemplates.length > 0 && (
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-text-primary mb-4">My Custom Templates</h4>
                    
                    {Object.entries(
                      customTemplates.reduce((acc, template) => {
                        const orgId = template.org_id || 'uncategorized';
                        if (!acc[orgId]) {
                          acc[orgId] = {
                            name: template.org_name || 'Uncategorized',
                            templates: []
                          };
                        }
                        acc[orgId].templates.push(template);
                        return acc;
                      }, {})
                    ).map(([orgId, { name: orgName, templates: orgTemplates }]) => (
                      <div key={orgId} className="mb-6">
                        <div 
                          className="flex items-center gap-2 mb-3 cursor-pointer hover:text-blue-600"
                          onClick={() => {
                            const newCollapsed = new Set(collapsedOrgs);
                            if (newCollapsed.has(orgId)) {
                              newCollapsed.delete(orgId);
                            } else {
                              newCollapsed.add(orgId);
                            }
                            setCollapsedOrgs(newCollapsed);
                          }}
                        >
                          {collapsedOrgs.has(orgId) ? (
                            <ChevronRight className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                          <h5 className="text-sm font-semibold text-text-primary">{orgName}</h5>
                          <span className="text-xs text-text-muted">({orgTemplates.length} template{orgTemplates.length !== 1 ? 's' : ''})</span>
                        </div>
                        
                        {!collapsedOrgs.has(orgId) && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {orgTemplates.map(template => (
                              <div
                                key={template.id}
                                className="border border-input rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                                onClick={() => {
                                  // Convert custom template to preview format
                                  const previewFormat = {
                                    name: template.template_name,
                                    description: template.description || '',
                                    icon: template.icon,
                                    category: template.category,
                                    source: template.org_name,
                                    tiers: template.tiers
                                  };
                                  
                                  setPreviewTemplateId(`custom_${template.id}`);
                                  setPreviewTemplate(previewFormat);
                                  
                                  // Initialize items
                                  const initialItems = {};
                                  template.tiers?.forEach((tier, tierIndex) => {
                                    const autoSelect = tierIndex === 0; // Auto-select first tier
                                    tier.items?.forEach((item, itemIndex) => {
                                      const itemKey = `${tierIndex}-${itemIndex}`;
                                      initialItems[itemKey] = {
                                        ...item,
                                        selected: autoSelect,
                                        quantity: autoSelect ? item.quantity : 0
                                      };
                                    });
                                  });
                                  
                                  setSelectedTemplateItems(initialItems);
                                  setShowTemplatePreview(true);
                                }}
                              >
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="text-3xl">{template.icon}</div>
                                  <div>
                                    <h5 className="font-semibold text-text-primary">{template.template_name}</h5>
                                    <p className="text-sm text-foreground/90">{template.category}</p>
                                  </div>
                                </div>
                                <p className="text-sm text-foreground/90">{template.description || 'No description'}</p>
                                <p className="text-xs text-green-600 mt-2">
                                  {template.tiers 
                                    ? `${template.tiers.length} tier${template.tiers.length !== 1 ? 's' : ''}, ${template.tiers.reduce((sum, tier) => sum + (tier.items?.length || 0), 0)} preset items`
                                    : '0 items'
                                  }
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Custom Kit */}
                <div>
                  <h4 className="text-lg font-semibold text-text-primary mb-4">Or Create Custom Kit</h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Kit Name *</label>
                        <input
                          type="text"
                          value={newKit.name}
                          onChange={(e) => setNewKit({ ...newKit, name: e.target.value })}
                          className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="e.g., My Custom Kit"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Icon</label>
                        <div className="relative flex gap-2">
                          <div className="flex-1 px-4 py-2 border border-input rounded-lg bg-surface-muted text-3xl text-center">
                            {newKit.icon}
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="px-4 py-2 border border-input rounded-lg hover:bg-surface-muted flex items-center gap-2"
                          >
                            <Smile className="w-5 h-5" />
                            Pick
                          </button>
                          {showEmojiPicker && (
                            <>
                              <div 
                                className="fixed inset-0 z-40" 
                                onClick={() => setShowEmojiPicker(false)}
                              />
                              <div className="absolute top-12 right-0 z-50 shadow-lg rounded-lg border border-border bg-card">
                                <EmojiPicker
                                  onEmojiClick={handleEmojiClick}
                                  autoFocusSearch={false}
                                  theme="light"
                                  width={300}
                                  height={400}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Category</label>
                        <select
                          value={newKit.category}
                          onChange={(e) => setNewKit({ ...newKit, category: e.target.value })}
                          className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="Custom">Custom</option>
                          <option value="Bug Out">Bug Out</option>
                          <option value="First Aid">First Aid</option>
                          <option value="Vehicle">Vehicle</option>
                          <option value="Home">Home</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Location</label>
                        <input
                          type="text"
                          value={newKit.location}
                          onChange={(e) => setNewKit({ ...newKit, location: e.target.value })}
                          className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="e.g., Garage"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Parent Kit
                        <span className="text-xs text-text-muted ml-2">(optional - makes this a subkit)</span>
                      </label>
                      <select
                        value={newKit.parent_kit_id || ''}
                        onChange={(e) => setNewKit({ ...newKit, parent_kit_id: e.target.value || null })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">None - Top Level Kit</option>
                        {kits.map(kit => (
                          <option key={kit.id} value={kit.id}>
                            {kit.icon} {kit.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">Description</label>
                      <textarea
                        value={newKit.description}
                        onChange={(e) => setNewKit({ ...newKit, description: e.target.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        rows="3"
                        placeholder="What is this kit for?"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleCreateCustomKit}
                        className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                      >
                        Create Kit
                      </button>
                      <button
                        onClick={() => setShowAddKit(false)}
                        className="px-6 py-2 bg-gray-200 text-text-primary rounded-lg hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Item Modal */}
        {showAddItem && selectedKit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-text-primary">Add Item to {selectedKit.name}</h3>
                  <button onClick={() => setShowAddItem(false)} className="text-text-muted hover:text-foreground/90">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Item Name *</label>
                  <input
                    type="text"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="e.g., Water Bottles"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Category</label>
                    <select
                      value={newItem.category === '' || categories.find(c => c.name === newItem.category) ? newItem.category : '__custom__'}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setNewItem({ ...newItem, category: '', subcategory: '' });
                        } else {
                          setNewItem({ ...newItem, category: e.target.value, subcategory: '' });
                        }
                      }}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {categories.map(cat => (
                        <option key={cat.name} value={cat.name}>{cat.name}</option>
                      ))}
                      <option value="__custom__">+ Custom Category...</option>
                    </select>
                    {(newItem.category === '' || !categories.find(c => c.name === newItem.category)) && newItem.category !== categories[0]?.name && (
                      <input
                        type="text"
                        value={newItem.category}
                        onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                        placeholder="Enter custom category name..."
                        autoFocus
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Subcategory</label>
                    {newItem.category && categories.find(c => c.name === newItem.category) ? (
                      // Predefined category - show dropdown with predefined subcategories
                      <>
                        <select
                          value={newItem.subcategory === '' || categories.find(c => c.name === newItem.category)?.subcategories.includes(newItem.subcategory) ? newItem.subcategory : '__custom__'}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              setNewItem({ ...newItem, subcategory: '__custom_input__' });
                            } else {
                              setNewItem({ ...newItem, subcategory: e.target.value });
                            }
                          }}
                          className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">None</option>
                          {categories.find(c => c.name === newItem.category)?.subcategories.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                          ))}
                          <option value="__custom__">+ Custom Subcategory...</option>
                        </select>
                        {(newItem.subcategory === '__custom_input__' || (newItem.subcategory !== '' && !categories.find(c => c.name === newItem.category)?.subcategories.includes(newItem.subcategory))) && (
                          <input
                            type="text"
                            value={newItem.subcategory === '__custom_input__' ? '' : newItem.subcategory}
                            onChange={(e) => setNewItem({ ...newItem, subcategory: e.target.value })}
                            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                            placeholder="Enter custom subcategory..."
                            autoFocus
                          />
                        )}
                      </>
                    ) : newItem.category ? (
                      // Custom category - show dropdown with only custom option
                      <>
                        <select
                          value={newItem.subcategory === '' ? '' : '__custom__'}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              setNewItem({ ...newItem, subcategory: '__custom_input__' });
                            } else {
                              setNewItem({ ...newItem, subcategory: '' });
                            }
                          }}
                          className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">None</option>
                          <option value="__custom__">+ Custom Subcategory...</option>
                        </select>
                        {(newItem.subcategory === '__custom_input__' || newItem.subcategory !== '') && (
                          <input
                            type="text"
                            value={newItem.subcategory === '__custom_input__' ? '' : newItem.subcategory}
                            onChange={(e) => setNewItem({ ...newItem, subcategory: e.target.value })}
                            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                            placeholder="Enter custom subcategory..."
                            autoFocus
                          />
                        )}
                      </>
                    ) : (
                      // No category selected - disable subcategory
                      <input
                        type="text"
                        value=""
                        disabled
                        className="w-full px-4 py-2 border border-input rounded-lg bg-gray-100 text-text-muted"
                        placeholder="Select a category first..."
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Quantity</label>
                    <input
                      type="number"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Unit</label>
                    <select
                      value={newItem.unit}
                      onChange={(e) => handleUnitChange(e.target.value, 'newItem')}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {allUnits.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                      <option value="__add_custom__">➕ Add Custom Unit...</option>
                    </select>
                    
                    {/* Custom Unit Input */}
                    {showCustomUnitInput && customUnitContext === 'newItem' && (
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="text"
                          value={customUnitInputValue}
                          onChange={(e) => setCustomUnitInputValue(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addCustomUnit(customUnitInputValue, 'newItem');
                            }
                          }}
                          placeholder="Enter unit name (e.g., pairs)"
                          className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => addCustomUnit(customUnitInputValue, 'newItem')}
                          className="px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 text-sm font-medium"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCustomUnitInput(false);
                            setCustomUnitInputValue('');
                            setCustomUnitContext(null);
                          }}
                          className="px-3 py-2 bg-gray-300 text-text-primary rounded-lg hover:bg-gray-400 text-sm font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Purchase Date</label>
                    <input
                      type="date"
                      value={newItem.purchase_date}
                      onChange={(e) => setNewItem({ ...newItem, purchase_date: e.target.value })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Expiration Date</label>
                    <input
                      type="date"
                      value={newItem.expiration_date}
                      onChange={(e) => setNewItem({ ...newItem, expiration_date: e.target.value })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Cost ($)</label>
                    <input
                      type="number"
                      value={newItem.cost}
                      onChange={(e) => setNewItem({ ...newItem, cost: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Low Stock Alert</label>
                    <input
                      type="number"
                      value={newItem.low_stock_threshold}
                      onChange={(e) => setNewItem({ ...newItem, low_stock_threshold: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      min="0"
                      step="0.1"
                      placeholder="0 = disabled"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Location</label>
                  <input
                    type="text"
                    value={newItem.location}
                    onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="e.g., Top shelf, Front pocket"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Notes</label>
                  <textarea
                    value={newItem.notes}
                    onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows="3"
                    placeholder="Any additional notes..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Product Link
                    <span className="text-xs text-text-muted ml-2">(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={newItem.product_url || ''}
                    onChange={(e) => setNewItem({ ...newItem, product_url: e.target.value })}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="https://example.com/product"
                  />
                  <p className="text-xs text-text-muted mt-1">Link to where this item can be purchased or more info</p>
                </div>

                {/* Maintenance Schedules */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-text-primary">Maintenance Schedules</h3>
                    <button
                      type="button"
                      onClick={() => addMaintenanceSchedule(false)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add Schedule
                    </button>
                  </div>

                  {(newItem.maintenance_schedules || []).length === 0 && (
                    <p className="text-sm text-text-muted italic py-2">No maintenance schedules added yet.</p>
                  )}

                  {(newItem.maintenance_schedules || []).map((schedule, index) => (
                    <div key={schedule.id} className="mb-4 p-4 bg-surface-muted rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-text-primary">Schedule #{index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeMaintenanceSchedule(schedule.id, false)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-foreground/90 mb-1">Type</label>
                            <select
                              value={schedule.type}
                              onChange={(e) => updateMaintenanceSchedule(schedule.id, 'type', e.target.value, false)}
                              className="w-full px-3 py-2 text-sm border border-input rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="replace">Replace</option>
                              <option value="test">Test</option>
                              <option value="rotate">Rotate</option>
                              <option value="inspect">Inspect</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-foreground/90 mb-1">Interval (days)</label>
                            <input
                              type="number"
                              value={schedule.interval_days}
                              onChange={(e) => updateMaintenanceSchedule(schedule.id, 'interval_days', parseInt(e.target.value, 10) || 0, false)}
                              className="w-full px-3 py-2 text-sm border border-input rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              min="1"
                              placeholder="90"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground/90 mb-1">Task Description</label>
                          <input
                            type="text"
                            value={schedule.notes || ''}
                            onChange={(e) => updateMaintenanceSchedule(schedule.id, 'notes', e.target.value, false)}
                            className="w-full px-3 py-2 text-sm border border-input rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="e.g., Replace AA batteries"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground/90 mb-1">Last Done (optional)</label>
                          <input
                            type="date"
                            value={schedule.last_done ? new Date(schedule.last_done).toISOString().split('T')[0] : ''}
                            onChange={(e) => {
                              const lastDone = e.target.value ? new Date(e.target.value).toISOString() : null;
                              updateMaintenanceSchedule(schedule.id, 'last_done', lastDone, false);
                              // Auto-calculate next_due
                              if (lastDone && schedule.interval_days) {
                                const nextDue = new Date(lastDone);
                                nextDue.setDate(nextDue.getDate() + schedule.interval_days);
                                updateMaintenanceSchedule(schedule.id, 'next_due', nextDue.toISOString(), false);
                              }
                            }}
                            className="w-full px-3 py-2 text-sm border border-input rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="text-xs text-foreground/90 bg-blue-50 p-2 rounded mt-2">
                    <strong>Tip:</strong> Add multiple schedules for different maintenance tasks (e.g., Replace batteries every 180 days, Test functionality every 90 days)
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleAddItem}
                    className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                  >
                    Add Item
                  </button>
                  <button
                    onClick={() => setShowAddItem(false)}
                    className="px-6 py-2 bg-gray-200 text-text-primary rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Item Modal */}
        {editingItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-text-primary">Edit Item</h3>
                  <button onClick={() => setEditingItem(null)} className="text-text-muted hover:text-foreground/90">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Item Name *</label>
                  <input
                    type="text"
                    value={editingItem.name}
                    onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Category</label>
                    <select
                      value={editingItem.category === '' || categories.find(c => c.name === editingItem.category) ? editingItem.category : '__custom__'}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setEditingItem({ ...editingItem, category: '', subcategory: '' });
                        } else {
                          setEditingItem({ ...editingItem, category: e.target.value, subcategory: '' });
                        }
                      }}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {categories.map(cat => (
                        <option key={cat.name} value={cat.name}>{cat.name}</option>
                      ))}
                      <option value="__custom__">+ Custom Category...</option>
                    </select>
                    {(editingItem.category === '' || !categories.find(c => c.name === editingItem.category)) && editingItem.category !== categories[0]?.name && (
                      <input
                        type="text"
                        value={editingItem.category}
                        onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                        placeholder="Enter custom category name..."
                        autoFocus
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Subcategory</label>
                    {editingItem.category && categories.find(c => c.name === editingItem.category) ? (
                      // Predefined category - show dropdown with predefined subcategories
                      <>
                        <select
                          value={editingItem.subcategory === '' || categories.find(c => c.name === editingItem.category)?.subcategories.includes(editingItem.subcategory) ? editingItem.subcategory : '__custom__'}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              setEditingItem({ ...editingItem, subcategory: '__custom_input__' });
                            } else {
                              setEditingItem({ ...editingItem, subcategory: e.target.value });
                            }
                          }}
                          className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">None</option>
                          {categories.find(c => c.name === editingItem.category)?.subcategories.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                          ))}
                          <option value="__custom__">+ Custom Subcategory...</option>
                        </select>
                        {(editingItem.subcategory === '__custom_input__' || (editingItem.subcategory !== '' && !categories.find(c => c.name === editingItem.category)?.subcategories.includes(editingItem.subcategory))) && (
                          <input
                            type="text"
                            value={editingItem.subcategory === '__custom_input__' ? '' : editingItem.subcategory}
                            onChange={(e) => setEditingItem({ ...editingItem, subcategory: e.target.value })}
                            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                            placeholder="Enter custom subcategory..."
                            autoFocus
                          />
                        )}
                      </>
                    ) : editingItem.category ? (
                      // Custom category - show dropdown with only custom option
                      <>
                        <select
                          value={editingItem.subcategory === '' ? '' : '__custom__'}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              setEditingItem({ ...editingItem, subcategory: '__custom_input__' });
                            } else {
                              setEditingItem({ ...editingItem, subcategory: '' });
                            }
                          }}
                          className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">None</option>
                          <option value="__custom__">+ Custom Subcategory...</option>
                        </select>
                        {(editingItem.subcategory === '__custom_input__' || editingItem.subcategory !== '') && (
                          <input
                            type="text"
                            value={editingItem.subcategory === '__custom_input__' ? '' : editingItem.subcategory}
                            onChange={(e) => setEditingItem({ ...editingItem, subcategory: e.target.value })}
                            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                            placeholder="Enter custom subcategory..."
                            autoFocus
                          />
                        )}
                      </>
                    ) : (
                      // No category selected - disable subcategory
                      <input
                        type="text"
                        value=""
                        disabled
                        className="w-full px-4 py-2 border border-input rounded-lg bg-gray-100 text-text-muted"
                        placeholder="Select a category first..."
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Quantity</label>
                    <input
                      type="number"
                      value={editingItem.quantity}
                      onChange={(e) => setEditingItem({ ...editingItem, quantity: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Unit</label>
                    <select
                      value={editingItem.unit}
                      onChange={(e) => handleUnitChange(e.target.value, 'editItem')}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {allUnits.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                      <option value="__add_custom__">➕ Add Custom Unit...</option>
                    </select>
                    
                    {/* Custom Unit Input */}
                    {showCustomUnitInput && customUnitContext === 'editItem' && (
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="text"
                          value={customUnitInputValue}
                          onChange={(e) => setCustomUnitInputValue(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addCustomUnit(customUnitInputValue, 'editItem');
                            }
                          }}
                          placeholder="Enter unit name (e.g., pairs)"
                          className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => addCustomUnit(customUnitInputValue, 'editItem')}
                          className="px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 text-sm font-medium"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCustomUnitInput(false);
                            setCustomUnitInputValue('');
                            setCustomUnitContext(null);
                          }}
                          className="px-3 py-2 bg-gray-300 text-text-primary rounded-lg hover:bg-gray-400 text-sm font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Purchase Date</label>
                    <input
                      type="date"
                      value={editingItem.purchase_date ? new Date(editingItem.purchase_date).toISOString().split('T')[0] : ''}
                      onChange={(e) => setEditingItem({ ...editingItem, purchase_date: e.target.value })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Expiration Date</label>
                    <input
                      type="date"
                      value={editingItem.expiration_date ? new Date(editingItem.expiration_date).toISOString().split('T')[0] : ''}
                      onChange={(e) => setEditingItem({ ...editingItem, expiration_date: e.target.value })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Cost ($)</label>
                    <input
                      type="number"
                      value={editingItem.cost}
                      onChange={(e) => setEditingItem({ ...editingItem, cost: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Low Stock Alert</label>
                    <input
                      type="number"
                      value={editingItem.low_stock_threshold}
                      onChange={(e) => setEditingItem({ ...editingItem, low_stock_threshold: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Location</label>
                  <input
                    type="text"
                    value={editingItem.location}
                    onChange={(e) => setEditingItem({ ...editingItem, location: e.target.value })}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Notes</label>
                  <textarea
                    value={editingItem.notes}
                    onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Product Link
                    <span className="text-xs text-text-muted ml-2">(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={editingItem.product_url || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, product_url: e.target.value })}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="https://example.com/product"
                  />
                  <p className="text-xs text-text-muted mt-1">Link to where this item can be purchased or more info</p>
                </div>

                {/* Maintenance Schedules */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-text-primary">Maintenance Schedules</h3>
                    <button
                      type="button"
                      onClick={() => addMaintenanceSchedule(true)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add Schedule
                    </button>
                  </div>

                  {((editingItem.maintenance_schedules || []).length === 0) && (
                    <p className="text-sm text-text-muted italic py-2">No maintenance schedules added yet.</p>
                  )}

                  {(editingItem.maintenance_schedules || []).map((schedule, index) => (
                    <div key={schedule.id} className="mb-4 p-4 bg-surface-muted rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-text-primary">Schedule #{index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeMaintenanceSchedule(schedule.id, true)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-foreground/90 mb-1">Type</label>
                            <select
                              value={schedule.type}
                              onChange={(e) => updateMaintenanceSchedule(schedule.id, 'type', e.target.value, true)}
                              className="w-full px-3 py-2 text-sm border border-input rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="replace">Replace</option>
                              <option value="test">Test</option>
                              <option value="rotate">Rotate</option>
                              <option value="inspect">Inspect</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-foreground/90 mb-1">Interval (days)</label>
                            <input
                              type="number"
                              value={schedule.interval_days}
                              onChange={(e) => updateMaintenanceSchedule(schedule.id, 'interval_days', parseInt(e.target.value, 10) || 0, true)}
                              className="w-full px-3 py-2 text-sm border border-input rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              min="1"
                              placeholder="90"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground/90 mb-1">Task Description</label>
                          <input
                            type="text"
                            value={schedule.notes || ''}
                            onChange={(e) => updateMaintenanceSchedule(schedule.id, 'notes', e.target.value, true)}
                            className="w-full px-3 py-2 text-sm border border-input rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="e.g., Replace AA batteries"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground/90 mb-1">Last Done</label>
                          <input
                            type="date"
                            value={schedule.last_done ? new Date(schedule.last_done).toISOString().split('T')[0] : ''}
                            onChange={(e) => {
                              const lastDone = e.target.value ? new Date(e.target.value).toISOString() : null;
                              updateMaintenanceSchedule(schedule.id, 'last_done', lastDone, true);
                              // Auto-calculate next_due
                              if (lastDone && schedule.interval_days) {
                                const nextDue = new Date(lastDone);
                                nextDue.setDate(nextDue.getDate() + schedule.interval_days);
                                updateMaintenanceSchedule(schedule.id, 'next_due', nextDue.toISOString(), true);
                              }
                            }}
                            className="w-full px-3 py-2 text-sm border border-input rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                          {schedule.next_due && (
                            <p className="text-xs text-blue-600 mt-1">
                              Next due: {new Date(schedule.next_due).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="text-xs text-foreground/90 bg-blue-50 p-2 rounded mt-2">
                    <strong>Tip:</strong> Add multiple schedules for different maintenance tasks
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleUpdateItem}
                    className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                  >
                    Update Item
                  </button>
                  <button
                    onClick={() => setEditingItem(null)}
                    className="px-6 py-2 bg-gray-200 text-text-primary rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transfer Item Modal */}
        {showTransferModal && transferItem && selectedKit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-text-primary">
                    {transferType === 'move' ? 'Move' : 'Copy'} Item
                  </h2>
                  <button
                    onClick={() => setShowTransferModal(false)}
                    className="text-text-muted hover:text-text-primary"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Item Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Package className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <h3 className="font-semibold text-text-primary">{transferItem.name}</h3>
                      <p className="text-sm text-foreground/90">Available: {transferItem.quantity} {transferItem.unit}</p>
                      <p className="text-sm text-foreground/90">From: {selectedKit.icon} {selectedKit.name}</p>
                    </div>
                  </div>
                </div>

                {/* Transfer Type Toggle */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text-primary mb-2">Transfer Type</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTransferType('move')}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                        transferType === 'move' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-text-primary hover:bg-surface-muted'
                      }`}
                    >
                      Move
                    </button>
                    <button
                      onClick={() => setTransferType('copy')}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                        transferType === 'copy' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-gray-100 text-text-primary hover:bg-surface-muted'
                      }`}
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-xs text-text-muted mt-1">
                    {transferType === 'move' 
                      ? 'Remove from source and add to destination' 
                      : 'Duplicate item to destination (keeps original)'}
                  </p>
                </div>

                {/* Destination Kit */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Destination Kit <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={transferDestinationKit}
                    onChange={(e) => setTransferDestinationKit(e.target.value)}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select destination kit...</option>
                    {kits.filter(k => k.id !== selectedKit.id).map(kit => (
                      <option key={kit.id} value={kit.id}>
                        {kit.icon} {kit.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantity */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={transferType === 'move' ? transferItem.quantity : undefined}
                    value={transferQuantity}
                    onChange={(e) => setTransferQuantity(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {transferType === 'move' && (
                    <p className="text-xs text-text-muted mt-1">
                      Maximum: {transferItem.quantity} {transferItem.unit}
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowTransferModal(false)}
                    className="flex-1 px-4 py-2 border border-input text-text-primary rounded-lg hover:bg-surface-muted"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTransferItem}
                    className={`flex-1 px-4 py-2 rounded-lg text-white ${
                      transferType === 'move' 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {transferType === 'move' ? 'Move Item' : 'Copy Item'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Kit Modal */}
        {editingKit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-text-primary">Edit Kit</h2>
                  <button
                    onClick={() => setEditingKit(null)}
                    className="text-text-muted hover:text-text-primary"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Kit Name</label>
                    <input
                      type="text"
                      value={editingKit.name}
                      onChange={(e) => setEditingKit({ ...editingKit, name: e.target.value })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="e.g., My Bug Out Bag"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Description</label>
                    <textarea
                      value={editingKit.description}
                      onChange={(e) => setEditingKit({ ...editingKit, description: e.target.value })}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows="3"
                      placeholder="Brief description of this kit"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Icon</label>
                    <div className="flex items-center gap-3">
                      <div className="text-4xl">{editingKit.icon}</div>
                      <button
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="px-4 py-2 border border-input rounded-lg hover:bg-surface-muted"
                      >
                        Change Icon
                      </button>
                    </div>
                    {showEmojiPicker && (
                      <div className="mt-2">
                        <EmojiPicker
                          onEmojiClick={(emojiObject) => {
                            setEditingKit({ ...editingKit, icon: emojiObject.emoji });
                            setShowEmojiPicker(false);
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">Category</label>
                      <select
                        value={editingKit.category}
                        onChange={(e) => setEditingKit({ ...editingKit, category: e.target.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="Custom">Custom</option>
                        <option value="Bug Out">Bug Out</option>
                        <option value="First Aid">First Aid</option>
                        <option value="Vehicle">Vehicle</option>
                        <option value="Home">Home</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">Location</label>
                      <input
                        type="text"
                        value={editingKit.location}
                        onChange={(e) => setEditingKit({ ...editingKit, location: e.target.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="e.g., Garage"
                      />
                    </div>
                  </div>

                  {!editingKit.is_general_stock && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Parent Kit
                        <span className="text-xs text-text-muted ml-2">(optional - makes this a subkit)</span>
                      </label>
                      <select
                        value={editingKit.parent_kit_id || ''}
                        onChange={(e) => {
                          const newParentId = e.target.value || null;
                          // Check for circular dependency
                          if (newParentId && wouldCreateCircularDependency(editingKit.id, newParentId)) {
                            toast.error('Cannot create circular dependency: kit cannot be its own ancestor');
                            return;
                          }
                          setEditingKit({ ...editingKit, parent_kit_id: newParentId });
                        }}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">None - Top Level Kit</option>
                        {kits.filter(k => k.id !== editingKit.id).map(kit => (
                          <option key={kit.id} value={kit.id}>
                            {kit.icon} {kit.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setEditingKit(null)}
                      className="flex-1 px-4 py-2 border border-input text-text-primary rounded-lg hover:bg-surface-muted"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        updateKit(editingKit.id, editingKit);
                        setEditingKit(null);
                      }}
                      className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Drill-Down Modal */}
        {drillDownView && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-text-primary">
                    {drillDownView === 'expired' && 'Expired Items'}
                    {drillDownView === 'expiring' && 'Items Expiring Soon'}
                    {drillDownView === 'lowStock' && 'Low Stock Items'}
                    {drillDownView === 'maintenanceOverdue' && 'Overdue Maintenance'}
                    {drillDownView === 'maintenanceDue' && 'Maintenance Due Soon'}
                  </h2>
                  <button
                    onClick={() => setDrillDownView(null)}
                    className="text-text-muted hover:text-text-primary"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-3">
                  {drillDownView === 'expired' && allItems.filter(item => getExpirationStatus(item.expiration_date) === 'expired').map(item => (
                    <div key={item.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-text-primary">{item.name}</div>
                          <div className="text-sm text-foreground/90">
                            From: {item.kitIcon} {item.kitName} • Expired: {item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : 'N/A'}
                          </div>
                          <div className="text-sm text-foreground/90">Quantity: {item.quantity} {item.unit}</div>
                        </div>
                        <div className="text-red-600 font-semibold">EXPIRED</div>
                      </div>
                    </div>
                  ))}
                  
                  {drillDownView === 'expiring' && allItems.filter(item => {
                    const status = getExpirationStatus(item.expiration_date);
                    return status === 'expiring-soon' || status === 'warning';
                  }).map(item => (
                    <div key={item.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-text-primary">{item.name}</div>
                          <div className="text-sm text-foreground/90">
                            From: {item.kitIcon} {item.kitName} • Expires: {item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : 'N/A'}
                          </div>
                          <div className="text-sm text-foreground/90">Quantity: {item.quantity} {item.unit}</div>
                        </div>
                        <div className="text-orange-600 font-semibold">EXPIRING</div>
                      </div>
                    </div>
                  ))}
                  
                  {drillDownView === 'lowStock' && allItems.filter(item => item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold).map(item => (
                    <div key={item.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-text-primary">{item.name}</div>
                          <div className="text-sm text-foreground/90">
                            From: {item.kitIcon} {item.kitName} • Quantity: {item.quantity} / {item.low_stock_threshold} {item.unit}
                          </div>
                          {item.cost > 0 && (
                            <div className="text-sm text-foreground/90">
                              Cost: ${item.cost} • Need: {Math.max(item.low_stock_threshold - item.quantity, 1)} more
                            </div>
                          )}
                        </div>
                        <div className="text-primary font-semibold">LOW STOCK</div>
                      </div>
                    </div>
                  ))}
                  
                  {drillDownView === 'maintenanceOverdue' && allItems.filter(item => getMaintenanceStatus(item) === 'overdue').map(item => (
                    <div key={item.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-text-primary">{item.name}</div>
                          <div className="text-sm text-foreground/90">
                            From: {item.kitIcon} {item.kitName}
                          </div>
                          <div className="text-sm text-foreground/90">
                            Due: {item.next_maintenance_date ? new Date(item.next_maintenance_date).toLocaleDateString() : 'N/A'} • Type: {item.maintenance_type || 'N/A'}
                          </div>
                        </div>
                        <div className="text-red-600 font-semibold">OVERDUE</div>
                      </div>
                    </div>
                  ))}
                  
                  {drillDownView === 'maintenanceDue' && allItems.filter(item => getMaintenanceStatus(item) === 'due-soon').map(item => (
                    <div key={item.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-text-primary">{item.name}</div>
                          <div className="text-sm text-foreground/90">
                            From: {item.kitIcon} {item.kitName}
                          </div>
                          <div className="text-sm text-foreground/90">
                            Due: {item.next_maintenance_date ? new Date(item.next_maintenance_date).toLocaleDateString() : 'N/A'} • Type: {item.maintenance_type || 'N/A'}
                          </div>
                        </div>
                        <div className="text-orange-600 font-semibold">DUE SOON</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Print Preview (Hidden on Screen, Visible on Print) */}
      {showPrintPreview && (
        <div className="print-only">
          <style>{`
            @media print {
              body * {
                visibility: hidden;
              }
              .print-only, .print-only * {
                visibility: visible;
              }
              .print-only {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
              }
              @page {
                margin: 0.5in;
              }
            }
            @media screen {
              .print-only {
                display: none;
              }
            }
          `}</style>

          {/* Kit Checklist Print */}
          {printType === 'kit' && selectedKit && (
            <div className="p-8 bg-card">
              <div className="border-b-2 border-gray-800 pb-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                      <span className="text-4xl">{selectedKit.icon}</span>
                      {selectedKit.name}
                    </h1>
                    <p className="text-foreground/90 mt-1">{selectedKit.description}</p>
                  </div>
                  <div className="text-right text-sm text-foreground/90">
                    <div>Printed: {new Date().toLocaleDateString()}</div>
                    {selectedKit.location && <div>Location: {selectedKit.location}</div>}
                    {selectedKit.last_audit_date && (
                      <div>Last Audit: {new Date(selectedKit.last_audit_date).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-bold text-text-primary mb-3">Kit Inventory Checklist</h2>
                <p className="text-sm text-foreground/90 mb-4">
                  Total Items: {selectedKit.items.length} | 
                  Use checkboxes to mark items during audit
                </p>
              </div>

              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-800">
                    <th className="text-left py-2 px-2 w-12">☐</th>
                    <th className="text-left py-2 px-2">Item Name</th>
                    <th className="text-left py-2 px-2">Category</th>
                    <th className="text-left py-2 px-2">Qty</th>
                    <th className="text-left py-2 px-2">Expiration</th>
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedKit.items.map((item, index) => {
                    const expirationStatus = getExpirationStatus(item.expiration_date);
                    return (
                      <tr key={item.id} className="border-b border-input">
                        <td className="py-2 px-2 text-center text-xl">☐</td>
                        <td className="py-2 px-2 font-medium">{item.name}</td>
                        <td className="py-2 px-2 text-sm">{item.category}</td>
                        <td className="py-2 px-2 text-sm">{item.quantity} {item.unit}</td>
                        <td className="py-2 px-2 text-sm">
                          {item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-2 px-2 text-sm">
                          {expirationStatus === 'expired' && '⚠️ EXPIRED'}
                          {expirationStatus === 'expiring-soon' && '⚠️ EXPIRING'}
                          {expirationStatus === 'warning' && '⚠️ CHECK'}
                          {expirationStatus === 'good' && '✓ Good'}
                          {expirationStatus === 'none' && '-'}
                        </td>
                        <td className="py-2 px-2 text-sm text-foreground/90">{item.notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="mt-8 pt-6 border-t-2 border-input">
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-bold text-text-primary mb-2">Audit Notes:</h3>
                    <div className="border border-input rounded h-24"></div>
                  </div>
                  <div>
                    <h3 className="font-bold text-text-primary mb-2">Items to Replace/Replenish:</h3>
                    <div className="border border-input rounded h-24"></div>
                  </div>
                </div>
                <div className="mt-6 flex justify-between text-sm text-foreground/90">
                  <div>Audited By: _______________________</div>
                  <div>Date: _______________________</div>
                  <div>Next Audit: _______________________</div>
                </div>
              </div>
            </div>
          )}

          {/* Master Inventory Report */}
          {printType === 'master' && (
            <div className="p-8 bg-card">
              <div className="border-b-2 border-gray-800 pb-4 mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Master Inventory Report</h1>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-foreground/90">Complete inventory across all emergency kits</p>
                  <div className="text-right text-sm text-foreground/90">
                    <div>Printed: {new Date().toLocaleDateString()}</div>
                    <div>Total Kits: {kits.length}</div>
                    <div>Total Items: {allItems.length}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-6 text-sm">
                <div className="border border-input rounded p-3">
                  <div className="text-foreground/90">Total Items</div>
                  <div className="text-2xl font-bold">{stats.totalItems}</div>
                </div>
                <div className="border border-input rounded p-3">
                  <div className="text-foreground/90">Expired</div>
                  <div className="text-2xl font-bold text-red-600">{stats.expiredItems}</div>
                </div>
                <div className="border border-input rounded p-3">
                  <div className="text-foreground/90">Expiring Soon</div>
                  <div className="text-2xl font-bold text-orange-600">{stats.expiringSoon}</div>
                </div>
                <div className="border border-input rounded p-3">
                  <div className="text-foreground/90">Total Value</div>
                  <div className="text-2xl font-bold text-green-600">${stats.totalValue.toFixed(0)}</div>
                </div>
              </div>

              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-800">
                    <th className="text-left py-2 px-2">Item</th>
                    <th className="text-left py-2 px-2">Kit</th>
                    <th className="text-left py-2 px-2">Category</th>
                    <th className="text-left py-2 px-2">Qty</th>
                    <th className="text-left py-2 px-2">Location</th>
                    <th className="text-left py-2 px-2">Expiration</th>
                    <th className="text-left py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const expirationStatus = getExpirationStatus(item.expiration_date);
                    return (
                      <tr key={item.id} className="border-b border-input">
                        <td className="py-2 px-2 font-medium">{item.name}</td>
                        <td className="py-2 px-2">{item.kitIcon} {item.kitName}</td>
                        <td className="py-2 px-2">{item.category}</td>
                        <td className="py-2 px-2">{item.quantity} {item.unit}</td>
                        <td className="py-2 px-2">{item.location || '-'}</td>
                        <td className="py-2 px-2">
                          {item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-2 px-2">
                          {expirationStatus === 'expired' && '⚠️ EXPIRED'}
                          {expirationStatus === 'expiring-soon' && '⚠️ EXPIRING'}
                          {expirationStatus === 'warning' && '⚠️ CHECK'}
                          {expirationStatus === 'good' && '✓'}
                          {expirationStatus === 'none' && '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="mt-6 text-xs text-text-muted">
                <p>Report generated by Emergency Supply Inventory Tool</p>
                <p>Review and update your inventory regularly to ensure preparedness</p>
              </div>
            </div>
          )}

          {/* Shopping List */}
          {printType === 'shopping' && (
            <div className="p-8 bg-card">
              <div className="border-b-2 border-gray-800 pb-4 mb-6">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <ShoppingCart className="w-8 h-8" />
                  Emergency Supply Shopping List
                </h1>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-foreground/90">Items below minimum stock threshold</p>
                  <div className="text-right text-sm text-foreground/90">
                    <div>Printed: {new Date().toLocaleDateString()}</div>
                    <div>Items to Purchase: {shoppingList.length}</div>
                  </div>
                </div>
              </div>

              <div className="mb-6 p-4 bg-orange-50 border border-orange-300 rounded">
                <p className="text-sm text-orange-800">
                  <strong>Important:</strong> These items have reached or fallen below their minimum stock levels. 
                  Replenish these supplies to maintain emergency preparedness.
                </p>
              </div>

              {/* Group by category */}
              {Object.entries(
                shoppingList.reduce((acc, item) => {
                  if (!acc[item.category]) acc[item.category] = [];
                  acc[item.category].push(item);
                  return acc;
                }, {})
              ).map(([category, items]) => (
                <div key={category} className="mb-6">
                  <h2 className="text-xl font-bold text-text-primary mb-3 pb-2 border-b border-gray-400">
                    {category} ({items.length} items)
                  </h2>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-input">
                        <th className="text-left py-2 px-2 w-12">☐</th>
                        <th className="text-left py-2 px-2">Item</th>
                        <th className="text-left py-2 px-2">Current Qty</th>
                        <th className="text-left py-2 px-2">Min Required</th>
                        <th className="text-left py-2 px-2">To Buy</th>
                        <th className="text-left py-2 px-2">Kit/Location</th>
                        <th className="text-left py-2 px-2">Est. Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const toBuy = Math.ceil(item.low_stock_threshold - item.quantity);
                        return (
                          <tr key={item.id} className="border-b border-border">
                            <td className="py-2 px-2 text-center text-xl">☐</td>
                            <td className="py-2 px-2 font-medium">{item.name}</td>
                            <td className="py-2 px-2 text-sm text-orange-600 font-semibold">
                              {item.quantity} {item.unit}
                            </td>
                            <td className="py-2 px-2 text-sm">
                              {item.low_stock_threshold} {item.unit}
                            </td>
                            <td className="py-2 px-2 text-sm font-bold">
                              {toBuy > 0 ? toBuy : item.low_stock_threshold} {item.unit}
                            </td>
                            <td className="py-2 px-2 text-sm text-foreground/90">
                              {item.kitIcon} {item.kitName}
                            </td>
                            <td className="py-2 px-2 text-sm">
                              {item.cost > 0 ? `$${(item.cost * (toBuy > 0 ? toBuy : item.low_stock_threshold)).toFixed(2)}` : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}

              <div className="mt-8 pt-6 border-t-2 border-input">
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-bold text-text-primary mb-2">Estimated Total Cost:</h3>
                    <div className="text-2xl font-bold text-green-600">
                      ${shoppingList.reduce((sum, item) => {
                        const toBuy = Math.ceil(Math.max(item.low_stock_threshold - item.quantity, item.low_stock_threshold));
                        return sum + (item.cost * toBuy);
                      }, 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-text-primary mb-2">Shopping Notes:</h3>
                    <div className="border border-input rounded h-20"></div>
                  </div>
                </div>
                <div className="mt-6 text-sm text-foreground/90">
                  <p>Date Purchased: _______________________</p>
                  <p className="mt-2 text-xs">Remember to update your inventory after purchasing these items.</p>
                </div>
              </div>
            </div>
          )}

          {/* Per-Kit Shopping List */}
          {printType === 'kit-shopping' && selectedKit && (
            <div className="p-8 bg-card">
              <div className="border-b-2 border-gray-800 pb-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                      <ShoppingCart className="w-8 h-8" />
                      Shopping List: {selectedKit.icon} {selectedKit.name}
                    </h1>
                    <p className="text-foreground/90 mt-1">Items to replenish for this kit</p>
                  </div>
                  <div className="text-right text-sm text-foreground/90">
                    <div>Printed: {new Date().toLocaleDateString()}</div>
                    <div>Items to Purchase: {selectedKit.items.filter(item => item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold).length}</div>
                    {selectedKit.location && <div>Kit Location: {selectedKit.location}</div>}
                  </div>
                </div>
              </div>

              <div className="mb-6 p-4 bg-orange-50 border border-orange-300 rounded">
                <p className="text-sm text-orange-800">
                  <strong>Shopping List for {selectedKit.name}:</strong> These items have reached or fallen below their minimum stock levels. 
                  Replenish these supplies to keep this kit fully stocked and ready.
                </p>
              </div>

              {(() => {
                const kitShoppingList = selectedKit.items.filter(item => 
                  item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold
                );
                
                // Group by category
                const groupedItems = kitShoppingList.reduce((acc, item) => {
                  if (!acc[item.category]) acc[item.category] = [];
                  acc[item.category].push(item);
                  return acc;
                }, {});

                return Object.entries(groupedItems).map(([category, items]) => (
                  <div key={category} className="mb-6">
                    <h2 className="text-xl font-bold text-text-primary mb-3 pb-2 border-b border-gray-400">
                      {category} ({items.length} items)
                    </h2>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-input">
                          <th className="text-left py-2 px-2 w-12">☐</th>
                          <th className="text-left py-2 px-2">Item</th>
                          <th className="text-left py-2 px-2">Current Qty</th>
                          <th className="text-left py-2 px-2">Min Required</th>
                          <th className="text-left py-2 px-2">To Buy</th>
                          <th className="text-left py-2 px-2">Location</th>
                          <th className="text-left py-2 px-2">Est. Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => {
                          const toBuy = Math.ceil(item.low_stock_threshold - item.quantity);
                          return (
                            <tr key={item.id} className="border-b border-border">
                              <td className="py-2 px-2 text-center text-xl">☐</td>
                              <td className="py-2 px-2 font-medium">{item.name}</td>
                              <td className="py-2 px-2 text-sm text-orange-600 font-semibold">
                                {item.quantity} {item.unit}
                              </td>
                              <td className="py-2 px-2 text-sm">
                                {item.low_stock_threshold} {item.unit}
                              </td>
                              <td className="py-2 px-2 text-sm font-bold">
                                {toBuy > 0 ? toBuy : item.low_stock_threshold} {item.unit}
                              </td>
                              <td className="py-2 px-2 text-sm text-foreground/90">
                                {item.location || '-'}
                              </td>
                              <td className="py-2 px-2 text-sm">
                                {item.cost > 0 ? `$${(item.cost * (toBuy > 0 ? toBuy : item.low_stock_threshold)).toFixed(2)}` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ));
              })()}

              <div className="mt-8 pt-6 border-t-2 border-input">
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-bold text-text-primary mb-2">Estimated Total Cost:</h3>
                    <div className="text-2xl font-bold text-green-600">
                      ${selectedKit.items
                        .filter(item => item.low_stock_threshold > 0 && item.quantity <= item.low_stock_threshold)
                        .reduce((sum, item) => {
                          const toBuy = Math.ceil(Math.max(item.low_stock_threshold - item.quantity, item.low_stock_threshold));
                          return sum + (item.cost * toBuy);
                        }, 0).toFixed(2)}
                    </div>
                    <p className="text-xs text-foreground/90 mt-2">For {selectedKit.name} only</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-text-primary mb-2">Shopping Notes:</h3>
                    <div className="border border-input rounded h-20"></div>
                  </div>
                </div>
                <div className="mt-6 text-sm text-foreground/90">
                  <p>Date Purchased: _______________________</p>
                  <p className="mt-2 text-xs">Remember to update "{selectedKit.name}" inventory after purchasing these items.</p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Use this focused shopping list when specifically restocking {selectedKit.name}. 
                  For a complete view of all low stock items across all kits, print the Master Shopping List from the Master Inventory view.
                </p>
              </div>
            </div>
          )}

          {/* Maintenance Schedule Report */}
          {printType === 'maintenance' && (
            <div className="p-8 bg-card">
              <div className="border-b-2 border-gray-800 pb-4 mb-6">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <AlertCircle className="w-8 h-8" />
                  Equipment Maintenance Schedule
                </h1>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-foreground/90">Preventive maintenance tracker for emergency equipment</p>
                  <div className="text-right text-sm text-foreground/90">
                    <div>Printed: {new Date().toLocaleDateString()}</div>
                    <div>Total Items: {allItems.filter(item => item.needs_maintenance).length}</div>
                  </div>
                </div>
              </div>

              {/* Overdue Maintenance */}
              {(() => {
                const overdueItems = allItems.filter(item => getMaintenanceStatus(item) === 'overdue');
                if (overdueItems.length === 0) return null;
                
                return (
                  <div className="mb-8">
                    <div className="bg-red-50 border border-red-300 p-4 mb-4">
                      <h2 className="text-xl font-bold text-red-900 mb-2">⚠️ OVERDUE MAINTENANCE ({overdueItems.length} items)</h2>
                      <p className="text-sm text-red-800">These items require immediate attention</p>
                    </div>
                    
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-gray-800">
                          <th className="text-left py-2 px-2 w-12">☐</th>
                          <th className="text-left py-2 px-2">Item</th>
                          <th className="text-left py-2 px-2">Kit</th>
                          <th className="text-left py-2 px-2">Type</th>
                          <th className="text-left py-2 px-2">Last Done</th>
                          <th className="text-left py-2 px-2">Was Due</th>
                          <th className="text-left py-2 px-2">Days Overdue</th>
                          <th className="text-left py-2 px-2">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overdueItems.map(item => {
                          const daysOverdue = item.next_maintenance_date 
                            ? Math.abs(Math.ceil((new Date(item.next_maintenance_date) - new Date()) / (1000 * 60 * 60 * 24)))
                            : 0;
                          return (
                            <tr key={item.id} className="border-b border-input bg-red-50">
                              <td className="py-2 px-2 text-center text-xl">☐</td>
                              <td className="py-2 px-2 font-medium">{item.name}</td>
                              <td className="py-2 px-2 text-sm">{item.kitIcon} {item.kitName}</td>
                              <td className="py-2 px-2 text-sm capitalize">{item.maintenance_type}</td>
                              <td className="py-2 px-2 text-sm">
                                {item.last_maintenance_date ? new Date(item.last_maintenance_date).toLocaleDateString() : 'Never'}
                              </td>
                              <td className="py-2 px-2 text-sm text-red-600 font-semibold">
                                {new Date(item.next_maintenance_date).toLocaleDateString()}
                              </td>
                              <td className="py-2 px-2 text-sm font-bold text-red-700">
                                {daysOverdue} days
                              </td>
                              <td className="py-2 px-2 text-sm text-foreground/90"></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Due Soon Maintenance */}
              {(() => {
                const dueSoonItems = allItems.filter(item => getMaintenanceStatus(item) === 'due-soon');
                if (dueSoonItems.length === 0) return null;
                
                return (
                  <div className="mb-8">
                    <div className="bg-orange-50 border border-orange-300 p-4 mb-4">
                      <h2 className="text-xl font-bold text-orange-900 mb-2">📅 DUE THIS WEEK ({dueSoonItems.length} items)</h2>
                      <p className="text-sm text-orange-800">Schedule these maintenance tasks within 7 days</p>
                    </div>
                    
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-gray-800">
                          <th className="text-left py-2 px-2 w-12">☐</th>
                          <th className="text-left py-2 px-2">Item</th>
                          <th className="text-left py-2 px-2">Kit</th>
                          <th className="text-left py-2 px-2">Type</th>
                          <th className="text-left py-2 px-2">Last Done</th>
                          <th className="text-left py-2 px-2">Due Date</th>
                          <th className="text-left py-2 px-2">Days Until</th>
                          <th className="text-left py-2 px-2">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dueSoonItems.map(item => {
                          const daysUntil = item.next_maintenance_date 
                            ? Math.ceil((new Date(item.next_maintenance_date) - new Date()) / (1000 * 60 * 60 * 24))
                            : 0;
                          return (
                            <tr key={item.id} className="border-b border-input bg-orange-50">
                              <td className="py-2 px-2 text-center text-xl">☐</td>
                              <td className="py-2 px-2 font-medium">{item.name}</td>
                              <td className="py-2 px-2 text-sm">{item.kitIcon} {item.kitName}</td>
                              <td className="py-2 px-2 text-sm capitalize">{item.maintenance_type}</td>
                              <td className="py-2 px-2 text-sm">
                                {item.last_maintenance_date ? new Date(item.last_maintenance_date).toLocaleDateString() : 'Never'}
                              </td>
                              <td className="py-2 px-2 text-sm text-orange-600 font-semibold">
                                {new Date(item.next_maintenance_date).toLocaleDateString()}
                              </td>
                              <td className="py-2 px-2 text-sm font-bold">
                                {daysUntil} days
                              </td>
                              <td className="py-2 px-2 text-sm text-foreground/90"></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Upcoming Maintenance */}
              {(() => {
                const upcomingItems = allItems.filter(item => getMaintenanceStatus(item) === 'upcoming');
                if (upcomingItems.length === 0) return null;
                
                return (
                  <div className="mb-8">
                    <div className="bg-blue-50 border border-blue-300 p-4 mb-4">
                      <h2 className="text-xl font-bold text-blue-900 mb-2">📋 UPCOMING (Next 30 Days) - {upcomingItems.length} items</h2>
                      <p className="text-sm text-blue-800">Plan ahead for these maintenance tasks</p>
                    </div>
                    
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-gray-400">
                          <th className="text-left py-2 px-2">Item</th>
                          <th className="text-left py-2 px-2">Kit</th>
                          <th className="text-left py-2 px-2">Type</th>
                          <th className="text-left py-2 px-2">Scheduled For</th>
                        </tr>
                      </thead>
                      <tbody>
                        {upcomingItems.map(item => (
                          <tr key={item.id} className="border-b border-border">
                            <td className="py-2 px-2">{item.name}</td>
                            <td className="py-2 px-2">{item.kitIcon} {item.kitName}</td>
                            <td className="py-2 px-2 capitalize">{item.maintenance_type}</td>
                            <td className="py-2 px-2">
                              {new Date(item.next_maintenance_date).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              <div className="mt-8 pt-6 border-t-2 border-input">
                <h3 className="font-bold text-text-primary mb-3">Maintenance Log</h3>
                <p className="text-sm text-foreground/90 mb-4">Use this space to record completed maintenance:</p>
                <div className="border border-input rounded h-32"></div>
                
                <div className="mt-6 text-xs text-text-muted">
                  <p><strong>Maintenance Types:</strong></p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li><strong>Replace:</strong> Swap out consumables (batteries, filters, bulbs)</li>
                    <li><strong>Test:</strong> Verify equipment functionality (radio, flashlight, fire extinguisher)</li>
                    <li><strong>Rotate:</strong> Replace with fresh supplies (food, water, medications)</li>
                    <li><strong>Inspect:</strong> Visual check for damage or deterioration</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Template Modal */}
      {showCreateTemplate && (
        <CreateTemplateModal 
          kits={kits.filter(k => !k.is_general_stock)}
          editingTemplate={editingTemplate}
          onClose={() => {
            setShowCreateTemplate(false);
            setEditingTemplate(null);
          }}
          onCreate={createTemplateFromKits}
          onUpdate={updateTemplate}
        />
      )}

      {/* Edit Organization Modal */}
      {showEditOrganization && editingOrganization && (
        <EditOrganizationModal
          organization={editingOrganization}
          onClose={() => {
            setShowEditOrganization(false);
            setEditingOrganization(null);
          }}
          onUpdate={updateOrganization}
        />
      )}

      {/* Import Template Modal */}
      {showImportTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg max-w-2xl w-full">
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Import Template</h2>
                  <p className="text-green-100 mt-1">Upload a template JSON file</p>
                </div>
                <button
                  onClick={() => setShowImportTemplate(false)}
                  className="text-primary-foreground hover:bg-white/20 rounded-full p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <p className="text-foreground/90 mb-4">
                  Import a template from a JSON file. The template will be added to your custom templates and will be available when creating new kits.
                </p>
                
                <div className="border-2 border-dashed border-input rounded-lg p-8 text-center">
                  <FileUp className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <label className="cursor-pointer">
                    <span className="text-primary hover:opacity-90 font-medium">Choose a file</span>
                    <input
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            try {
                              const jsonData = event.target?.result;
                              if (importTemplate(jsonData)) {
                                setShowImportTemplate(false);
                              }
                            } catch (error) {
                              toast.error('Failed to read file');
                            }
                          };
                          reader.readAsText(file);
                        }
                      }}
                    />
                  </label>
                  <p className="text-text-muted text-sm mt-2">or drag and drop</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">Expected JSON Format:</h3>
                <pre className="text-xs text-text-primary bg-card p-3 rounded border border-border overflow-x-auto">
{`{
  "org_id": "wahle_tec",
  "org_name": "Wahle-Tec",
  "template_id": "ifak",
  "template_name": "IFAK",
  "icon": "🩹",
  "category": "Medical",
  "tiers": [
    {
      "name": "Minimal",
      "description": "...",
      "order": 1,
      "items": [...]
    }
  ]
}`}
                </pre>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowImportTemplate(false)}
                  className="px-4 py-2 text-foreground/90 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Configuration Modal */}
      {showTemplatePreview && previewTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-primary text-primary-foreground p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-5xl">{previewTemplate.icon}</span>
                  <div>
                    <h2 className="text-2xl font-bold">{previewTemplate.name}</h2>
                    <p className="text-foreground/80 mt-1">{previewTemplate.description}</p>
                    <p className="text-sm text-primary-foreground/90 mt-1">Source: {previewTemplate.source}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTemplatePreview(false)}
                  className="text-primary-foreground hover:bg-white/20 rounded-full p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Tier-based Configuration */}
              {previewTemplate.tiers ? (
                <div className="space-y-6">
                  {/* Configuration Level Buttons */}
                  <div className="grid grid-cols-4 gap-3">
                    {previewTemplate.tiers.map((tier, tierIndex) => {
                      // Map tier names to button labels
                      const buttonLabels = {
                        'Absolute Minimal': 'Minimal',
                        'Base Kit': 'Base',
                        'Recommended Additions': 'Expanded',
                        'Advanced Medical Training': 'Advanced'
                      };
                      const buttonLabel = buttonLabels[tier.name] || tier.name.split(' ')[0];
                      const isActive = activeConfigTier === tierIndex;
                      
                      return (
                        <button
                          key={tierIndex}
                          onClick={() => {
                            // Set active tier
                            setActiveConfigTier(tierIndex);
                            
                            // Apply this tier's configuration to all items
                            const updates = {};
                            previewTemplate.tiers.forEach((t, tIdx) => {
                              // Safety check: ensure tier.items exists and is an array
                              if (t.items && Array.isArray(t.items)) {
                                t.items.forEach((item, iIdx) => {
                                  const itemKey = `${tIdx}-${iIdx}`;
                                  // If this is the selected tier or a lower tier, include the item
                                  const shouldInclude = tIdx <= tierIndex;
                                  updates[itemKey] = {
                                    ...item,
                                    selected: shouldInclude,
                                    quantity: shouldInclude ? (item.quantity || 1) : 0
                                  };
                                });
                              }
                            });
                            setSelectedTemplateItems(updates);
                          }}
className={`px-4 py-3 rounded-lg font-semibold transition-all border-2 hover:shadow-md ${
                              isActive 
                              ? 'bg-primary border-primary text-primary-foreground' 
                              : 'bg-card border-border text-text-primary hover:bg-surface-muted'
                          }`}
                        >
                          {buttonLabel}
                        </button>
                      );
                    })}
                  </div>

                  {/* Configuration Description */}
                  {previewTemplate.description ? (
                    <div className="bg-primary/10 border-l-4 border-primary p-4 rounded-r-lg">
                      <div className="flex items-start gap-2">
                        <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-text-primary mb-2">Usage Notes</h4>
                          <p className="text-sm text-foreground/90 whitespace-pre-line">{previewTemplate.description}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-primary/10 border-l-4 border-primary p-4 rounded-r-lg">
                      <p className="text-sm text-foreground/90">
                        Click a configuration level above to automatically select items and set quantities for that tier.
                      </p>
                    </div>
                  )}

                  {/* Consolidated Item List */}
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-4">Kit Items</h3>
                    <div className="space-y-3">
                      {(() => {
                        // Build consolidated item list with all tier occurrences
                        const consolidatedItems = [];
                        const itemOccurrences = {}; // Track all occurrences of each item
                        
                        previewTemplate.tiers.forEach((tier, tierIndex) => {
                          // Safety check: ensure tier.items exists and is an array
                          if (tier.items && Array.isArray(tier.items)) {
                            tier.items.forEach((item, itemIndex) => {
                              const itemKey = `${tierIndex}-${itemIndex}`;
                              if (!itemOccurrences[item.name]) {
                                itemOccurrences[item.name] = [];
                              }
                              itemOccurrences[item.name].push({
                                ...item,
                                itemKey,
                                tierIndex,
                                tierName: tier.name
                              });
                            });
                          }
                        });
                        
                        // Create one entry per unique item
                        Object.keys(itemOccurrences).forEach(itemName => {
                          const occurrences = itemOccurrences[itemName];
                          // Use the first occurrence for display
                          consolidatedItems.push({
                            ...occurrences[0],
                            allOccurrences: occurrences
                          });
                        });

                        return consolidatedItems.map((item) => {
                          // Check if any occurrence of this item is selected
                          const selectedOccurrences = item.allOccurrences.filter(occ => 
                            selectedTemplateItems[occ.itemKey]?.selected !== false &&
                            (selectedTemplateItems[occ.itemKey]?.quantity ?? 0) > 0
                          );
                          
                          const isSelected = selectedOccurrences.length > 0;
                          
                          // Get the highest tier quantity that's selected
                          const quantity = isSelected
                            ? Math.max(...selectedOccurrences.map(occ => 
                                selectedTemplateItems[occ.itemKey]?.quantity ?? occ.quantity
                              ))
                            : 0;
                          
                          return (
                            <div key={item.itemKey} className={`border rounded-lg p-4 transition-all ${isSelected ? 'border-primary bg-primary/10' : 'border-border bg-card'}`}>
                              <div className="flex items-start gap-3">
                                {/* Checkbox */}
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    // Update all occurrences of this item
                                    const updates = {};
                                    item.allOccurrences.forEach(occ => {
                                      updates[occ.itemKey] = {
                                        ...occ,
                                        selected: e.target.checked,
                                        quantity: e.target.checked ? occ.quantity : 0
                                      };
                                    });
                                    setSelectedTemplateItems({
                                      ...selectedTemplateItems,
                                      ...updates
                                    });
                                  }}
                                  className="w-5 h-5 text-primary rounded focus:ring-primary mt-1 flex-shrink-0"
                                />
                                
                                {/* Item Details */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-gray-900">{item.name}</h4>
                                      {item.note && (
                                        <p className="text-sm text-foreground/90 mt-1">{item.note}</p>
                                      )}
                                      
                                      {/* Item Meta */}
                                      <div className="flex items-center gap-2 mt-2 text-xs text-text-muted flex-wrap">
                                        <span>{item.category}</span>
                                        <span>•</span>
                                        <span>${(item.cost || 0).toFixed(2)}</span>
                                        {(item.maintenance_schedules || []).length > 0 && (
                                          <>
                                            <span>•</span>
                                            <span>{item.maintenance_schedules.length} maintenance schedule(s)</span>
                                          </>
                                        )}
                                        {item.product_url && (
                                          <>
                                            <span>•</span>
                                            <a
                                              href={item.product_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              View Product
                                              <ExternalLink className="w-3 h-3" />
                                            </a>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Quantity Controls */}
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => {
                                          const newQty = Math.max(0, quantity - 1);
                                          // Update the highest tier occurrence
                                          const highestTierOcc = item.allOccurrences[item.allOccurrences.length - 1];
                                          setSelectedTemplateItems({
                                            ...selectedTemplateItems,
                                            [highestTierOcc.itemKey]: {
                                              ...highestTierOcc,
                                              selected: newQty > 0,
                                              quantity: newQty
                                            }
                                          });
                                        }}
                                        className="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded"
                                      >
                                        -
                                      </button>
                                      <div className="w-16 text-center">
                                        <div className={`text-xl font-bold ${isSelected ? 'text-blue-600' : 'text-text-muted'}`}>
                                          {quantity}
                                        </div>
                                        <div className="text-xs text-text-muted">{item.unit}</div>
                                      </div>
                                      <button
                                        onClick={() => {
                                          const newQty = quantity + 1;
                                          // Update the highest tier occurrence
                                          const highestTierOcc = item.allOccurrences[item.allOccurrences.length - 1];
                                          setSelectedTemplateItems({
                                            ...selectedTemplateItems,
                                            [highestTierOcc.itemKey]: {
                                              ...highestTierOcc,
                                              selected: true,
                                              quantity: newQty
                                            }
                                          });
                                        }}
                                        className="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              ) : previewTemplate.items && Array.isArray(previewTemplate.items) ? (
                /* Fallback for old flat templates - render items directly */
                <div className="space-y-6">
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                    <p className="text-sm text-blue-900">
                      This template uses the classic format. All items are shown below.
                    </p>
                  </div>
                  
                  <div className="border border-input rounded-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-4 py-3">
                      <h3 className="font-semibold text-lg">Kit Items</h3>
                      <p className="text-sm text-gray-300 mt-1">Select items to include in your kit</p>
                    </div>
                    
                    {/* Items List */}
                    <div className="bg-card">
                      {previewTemplate.items.map((item, itemIndex) => {
                        const itemKey = `0-${itemIndex}`;
                        const isSelected = selectedTemplateItems[itemKey]?.selected !== false;
                        const quantity = selectedTemplateItems[itemKey]?.quantity || item.quantity || 1;
                        
                        return (
                          <div key={itemKey} className={`border-b border-border last:border-b-0 ${isSelected ? 'bg-card' : 'bg-surface-muted'}`}>
                            <div className="p-4">
                              <div className="flex items-start gap-3">
                                {/* Checkbox */}
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    setSelectedTemplateItems({
                                      ...selectedTemplateItems,
                                      [itemKey]: {
                                        ...item,
                                        selected: e.target.checked,
                                        quantity: e.target.checked ? (item.quantity || 1) : 0
                                      }
                                    });
                                  }}
                                  className="w-5 h-5 text-blue-600 rounded focus:ring-primary mt-1"
                                />
                                
                                {/* Item Details */}
                                <div className="flex-1">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-gray-900">{item.name}</h4>
                                      {(item.note || item.notes) && (
                                        <p className="text-sm text-foreground/90 mt-1">{item.note || item.notes}</p>
                                      )}
                                      <div className="flex items-center gap-2 mt-2 text-xs text-text-muted">
                                        <span>{item.category}</span>
                                        {item.cost && (
                                          <>
                                            <span>•</span>
                                            <span>${(item.cost || 0).toFixed(2)}</span>
                                          </>
                                        )}
                                        {(item.maintenance_schedules || []).length > 0 && (
                                          <>
                                            <span>•</span>
                                            <span>{item.maintenance_schedules.length} maintenance schedule(s)</span>
                                          </>
                                        )}
                                        {item.product_url && (
                                          <>
                                            <span>•</span>
                                            <a 
                                              href={item.product_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              View Product
                                              <ExternalLink className="w-3 h-3" />
                                            </a>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Quantity Control */}
                                    <div className="ml-4 flex items-center gap-2">
                                      <button
                                        onClick={() => {
                                          const newQty = Math.max(0, quantity - 1);
                                          setSelectedTemplateItems({
                                            ...selectedTemplateItems,
                                            [itemKey]: {
                                              ...item,
                                              selected: newQty > 0,
                                              quantity: newQty
                                            }
                                          });
                                        }}
                                        className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded"
                                      >
                                        -
                                      </button>
                                      <div className="w-16 text-center">
                                        <div className={`font-medium ${isSelected ? 'text-gray-900' : 'text-text-muted'}`}>{quantity}</div>
                                        <div className="text-xs text-text-muted">{item.unit || 'items'}</div>
                                      </div>
                                      <button
                                        onClick={() => {
                                          const newQty = quantity + 1;
                                          setSelectedTemplateItems({
                                            ...selectedTemplateItems,
                                            [itemKey]: {
                                              ...item,
                                              selected: true,
                                              quantity: newQty
                                            }
                                          });
                                        }}
                                        className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-surface-muted rounded-lg border border-border">
                  <p className="p-4 text-foreground/90">No items available in this template.</p>
                </div>
              )}

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="bg-card rounded-lg p-4 border border-border">
                  <p className="text-sm text-foreground/90 font-medium">Selected Items</p>
                  <p className="text-2xl font-bold text-text-primary">
                    {(() => {
                      // Consolidate items to get accurate count (same logic as kit creation)
                      const selectedItems = Object.values(selectedTemplateItems)
                        .filter(item => item.selected !== false && (item.quantity || 0) > 0);
                      const consolidatedMap = {};
                      selectedItems.forEach(item => {
                        if (!consolidatedMap[item.name]) {
                          consolidatedMap[item.name] = item;
                        } else {
                          const existingQty = consolidatedMap[item.name].quantity || 0;
                          const newQty = item.quantity || 0;
                          if (newQty > existingQty) {
                            consolidatedMap[item.name] = item;
                          }
                        }
                      });
                      return Object.keys(consolidatedMap).length;
                    })()}
                  </p>
                </div>
                <div className="bg-card rounded-lg p-4 border border-border">
                  <p className="text-sm text-foreground/90 font-medium">Estimated Cost</p>
                  <p className="text-2xl font-bold text-primary">
                    ${(() => {
                      // Consolidate items to get accurate cost
                      const selectedItems = Object.values(selectedTemplateItems)
                        .filter(item => item.selected !== false && (item.quantity || 0) > 0);
                      const consolidatedMap = {};
                      selectedItems.forEach(item => {
                        if (!consolidatedMap[item.name]) {
                          consolidatedMap[item.name] = item;
                        } else {
                          const existingQty = consolidatedMap[item.name].quantity || 0;
                          const newQty = item.quantity || 0;
                          if (newQty > existingQty) {
                            consolidatedMap[item.name] = item;
                          }
                        }
                      });
                      return Object.values(consolidatedMap)
                        .reduce((sum, item) => sum + ((item.cost || 0) * (item.quantity || 0)), 0)
                        .toFixed(2);
                    })()}
                  </p>
                </div>
                <div className="bg-card rounded-lg p-4 border border-border">
                  <p className="text-sm text-foreground/90 font-medium">Total Quantity</p>
                  <p className="text-2xl font-bold text-text-primary">
                    {(() => {
                      // Consolidate items to get accurate total quantity
                      const selectedItems = Object.values(selectedTemplateItems)
                        .filter(item => item.selected !== false && (item.quantity || 0) > 0);
                      const consolidatedMap = {};
                      selectedItems.forEach(item => {
                        if (!consolidatedMap[item.name]) {
                          consolidatedMap[item.name] = item;
                        } else {
                          const existingQty = consolidatedMap[item.name].quantity || 0;
                          const newQty = item.quantity || 0;
                          if (newQty > existingQty) {
                            consolidatedMap[item.name] = item;
                          }
                        }
                      });
                      return Object.values(consolidatedMap)
                        .reduce((sum, item) => sum + (item.quantity || 0), 0);
                    })()}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="bg-surface-muted px-6 py-4 border-t border-border flex items-center justify-between">
              <button
                onClick={() => setShowTemplatePreview(false)}
                className="px-4 py-2 text-text-primary hover:bg-surface-muted rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Create kit with consolidated items (no duplicates)
                  const selectedItems = Object.values(selectedTemplateItems)
                    .filter(item => item.selected !== false && (item.quantity || 0) > 0);
                  
                  if (selectedItems.length === 0) {
                    toast.error('Please select at least one item with quantity > 0');
                    return;
                  }
                  
                  // Consolidate items by name - keep highest quantity for each unique item
                  const consolidatedMap = {};
                  selectedItems.forEach(item => {
                    if (!consolidatedMap[item.name]) {
                      consolidatedMap[item.name] = {
                        ...item,
                        quantity: item.quantity || 1
                      };
                    } else {
                      // Item already exists - use the higher quantity
                      const existingQty = consolidatedMap[item.name].quantity;
                      const newQty = item.quantity || 1;
                      if (newQty > existingQty) {
                        consolidatedMap[item.name] = {
                          ...item,
                          quantity: newQty
                        };
                      }
                    }
                  });
                  
                  const consolidatedItems = Object.values(consolidatedMap);
                  
                  // Create customized kit
                  const customizedTemplate = {
                    ...previewTemplate,
                    items: consolidatedItems
                  };
                  
                  const kit = {
                    name: customizedTemplate.name,
                    description: customizedTemplate.description,
                    icon: customizedTemplate.icon,
                    category: customizedTemplate.category,
                    location: '',
                    parent_kit_id: null,
                    items: consolidatedItems
                  };
                  
                  createKit(kit);
                  setShowTemplatePreview(false);
                  setShowAddKit(false);
                  toast.success(`${customizedTemplate.name} created with ${selectedItems.length} items`);
                }}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Customized Kit
              </button>
            </div>
          </div>
        </div>
      )}
      </div></>
    </ToolLayout>
  );
};

export default InventoryTool;
